import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, LayersControl, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Button, buttonVariants } from "../components/ui/button";
import TapBoxButton from "../components/TapBoxButton";
import BasemapTapBox from "../components/BasemapTapBox";
import { MapPin, Filter, Search, MapPinPlus, Bookmark, BookmarkCheck, Globe2, ShieldCheck, User as UserIcon, SearchX, LocateFixed, X, Pencil, LayoutGrid, Lightbulb, Home, Archive, EyeOff, TriangleAlert, RotateCw } from "lucide-react";
import GroundSearchDialog from "../components/GroundSearchDialog";
// Issue #766 — aliased on import: this FILE's own module-private `GroundCard`
// (below, the shipped DIRECTORY card component) already owns that name; the
// new full-page screen is `components/GroundCard.jsx`'s default export.
import GroundCardScreen from "../components/GroundCard";
import useLaptop from "../lib/useLaptop";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
// #548: the pin-a-surface machinery — and `PinDropCatcher`, which moved
// there so both the ground gestures (#57/#379) and the surface one share
// the identical catcher rather than a copy.
import {
  PinDropCatcher,
  SurfacePinHint,
  SurfacePinLayer,
  useSurfacePinDraft,
} from "../components/SurfacePinMap";
import GroundEditPanel from "../components/GroundEditPanel";
import ConditionUpdate, { ConditionBadge } from "../components/ConditionUpdate";
import FacilityRatingPanel, { useGroundRatings } from "../components/FacilityRatings";
import {
  GROUND_PIN_FROM_MAP,
  PRIVATE_GROUND_LABEL,
  canOfferGroundEdit,
  groundPin,
  isGroundPin,
  isPrivateGround,
} from "../lib/groundEdit";
import { canUpdateCondition } from "../lib/conditionUpdate";
import FacilityAddForm from "../components/FacilityAddForm";
import FacilityEditForm from "../components/FacilityEditForm";
import { FACILITY_EMPTY_COPY, SURFACE_INSTEAD_NOTE, canAddFacility, facilityEmptyState, upsertFacilityRow } from "../lib/facilityAdd";
import { ARCHIVED_LABEL, isArchived } from "../lib/archived";
import {
  DIRECTORY_ERROR_BODY,
  DIRECTORY_ERROR_TITLE,
  DIRECTORY_RETRY_LABEL,
  directoryOutcome,
} from "../lib/directoryLoad";
// #643: the viewport contract's client half. Every DECISION is in the lib
// (the snap, the cache key, the mode, the truncation sentence);
// `useDirectoryViewport` is the React state and sequencing around it, and
// this page is the wiring — leaflet events in, components out.
import { useDirectoryViewport } from "../hooks/useDirectoryViewport";
// #644: the SEARCH's client half, same seam. Every decision is in
// `lib/directorySearch.js`; the hook is the debounce, the page number and the
// late-answer rule; this page is the wiring.
import { useDirectorySearch } from "../hooks/useDirectorySearch";
import {
  GROUNDS_SEARCH_MAX_LENGTH,
  chipCountLabel,
  clampPage,
  describeDirectoryPage,
  directoryListing,
  listingResetKey,
  searchSummaryScope,
} from "../lib/directorySearch";
// #683: the unified-directory merge (directory rows + everywhere played +
// everywhere saved, with the archived-venue rule and the principle behind
// it) — see the hook comment above `enriched` below.
import { enrichDirectory } from "../lib/directoryEnrich";
import {
  MAP_MOUNT_ZOOM,
  VIEWPORT_DEBOUNCE_MS,
  cellFit,
  viewportFromMap,
  worldViewport,
} from "../lib/mapViewport";
import ArchiveToggle from "../components/ArchiveToggle";
import { MapFitter } from "../components/MapFitter";
import { useGroundsView } from "../hooks/useGroundsView";
import { NZ_FALLBACK_CENTER, applyFit, computeCentredFit, computeMapFit, coordsOf, fitAnchorLng, fitSignature, hasCoords, newLookMapProps, placeCells, placeLatLng, pushClearOfControls, readControlRects, wrapLngNear } from "../lib/mapFit";
import WrappingTileLayer from "../components/WrappingTileLayer";
import LiveCentreLng from "../components/LiveCentreLng";
import MapFullScreenSync from "../components/kit/MapFullScreenSync";
import MapControlPill from "../components/kit/MapControlPill";
import { track } from "../lib/usageTracker";
import { TAP_LINK_STYLE } from "../components/kit/tapTarget";
// #617: the answer to a search, said above the map instead of only below it.
import { mapBlockOrderClass, scrollResultsIntoView, searchResultSummary } from "../lib/searchResultSummary";
import { NEAR_ME_BUSY_LABEL, NEAR_ME_LABEL, NEAR_ME_RADIUS_KM, findNearMe } from "../lib/nearMe";
// #643: the near-me ranking is re-run as the viewport's rows change — see
// `nearMeResults`. The device read stays in `findNearMe`; this is only the
// arithmetic over what is loaded.
import { nearestGrounds } from "../lib/distance";
import { PLACE_TYPE_FILTERS, PLACE_TYPE_META, placeTypeGlyph, placeTypeLabel } from "../lib/placeType";
import {
  FACILITY_COLOR,
  facilitiesByGround,
  facilityCount,
  facilityMarkers,
  facilityKindLabel,
  facilitySummaryLabel,
  facilitySurfaceGlyph,
  facilitySurfaceLabel,
  hasFacilityPin,
  sortFacilitiesByKind,
} from "../lib/facilities";
import {
  BASEMAPS,
  DETAIL_DEFAULT_BASEMAP,
  DIRECTORY_DEFAULT_BASEMAP,
  basemapIdFromLabel,
  readBasemap,
  resolveBasemap,
  writeBasemap,
} from "../lib/basemap";
import { toast } from "sonner";
// Issue #765 — "grounds near you" (wave 5, slice 1), behind
// `newUi("grounds")`. `GroundsNearYouScreen`/`useGroundsNearYou` are this
// screen's own presentation and data hook — see each file's own header for
// why they are split out rather than grown inside this component, and for
// where the "the rendered screen replaces the page" chrome mechanism
// (#757/#760) now lives.
import { newUi } from "../lib/featureFlags";
import { useGroundsNearYou } from "../hooks/useGroundsNearYou";
import GroundsNearYouScreen from "../components/GroundsNearYouScreen";
import GroundOversLeftOut from "../components/GroundOversLeftOut";
import { buildStatsByName } from "../lib/groundCareerStats";

/*
 * Restored from git history (914cc6f~1, issue #6/#7) for issue #58. The
 * prototype's grounds API is gone — issue #57 replaced it with a stricter
 * D1-backed one — so this page is rewired against the REAL shapes:
 *
 *  - GET /api/grounds            -> { ground_id, name, city, country,
 *      latitude, longitude, is_mine, is_global, is_verified,
 *      created_at, updated_at }[] — no server-side `q` filter, so search
 *      is done client-side (see `filtered` below). Issue #677: `created_by`
 *      is gone from this player-facing shape (a raw user id told a player
 *      nothing and let anybody correlate every ground one account had
 *      touched); `is_mine` is the server's own answer to "did I create
 *      this", computed against the caller rather than left for the client
 *      to derive from an id it should never have received.
 *  - GET /api/players/me/grounds -> bookmarked grounds (`ground_id`, `name`,
 *      ...); GET returns 404 for a caller with no player profile (coach)
 *      — handled by only fetching it for the player role.
 *  - GET /api/venues/played      -> { ground, matches, ground_id, city,
 *      country, latitude, longitude }[] — this is a *count*, not the
 *      batting/bowling record (that lived on the old response but doesn't
 *      exist here).
 *  - GET /api/analytics/career   -> `batting.by_ground` / `bowling.by_ground`
 *      (statsCompute.ts) is where the player's record per ground actually
 *      lives now. Wired in below rather than recomputed (issue #58 requirement).
 *
 * `/venues/played` and `/analytics/career` both require a `player_id` for
 * coach/admin callers (see `routes/authz.ts` `resolveScope`) and this page
 * never supplies one, so they're only fetched for the player role — a
 * coach still gets the full directory + search + add-ground flow, just no
 * personal "played here" stats layered on top.
 *
 * Issue #134 adds "Use my location" here. It reads the device position once
 * per press, ranks the directory THIS PAGE HAS ALREADY DOWNLOADED on the
 * device (`lib/nearMe.js` over `lib/distance.js`'s haversine, #133), and
 * composes with what is already on screen rather than replacing it: the
 * results are one more constraint applied after the search box and the
 * chips, and the map moves by adding the device position to the point set
 * #212's `computeMapFit`/`applyFit` already fit to — so one press produces a
 * view holding you and your nearest grounds, with #212's reduced-motion rule
 * intact. Nothing carries the position off the device; see `lib/nearMe.js`
 * for the privacy argument and the assertions that hold it.
 *
 * Issue #136 puts the PLACE TYPE on this page, and it had to fit into a
 * marker that was already carrying a meaning. The pins encode two different
 * facts now, on two different channels: COLOUR still says whose ground this
 * is (played / yours / global — the legend #57 established and #134's teal
 * "you" marker sits beside), and the GLYPH says what kind of place it is
 * (F/N/I, `lib/placeType.js`). The glyph was free to take: it used to spell
 * P/G/U, which restated the colour and told a reader nothing the colour had
 * not. So a nets pin beside a field pin is now distinguishable at a glance
 * even when both are the same colour — which is the literal thing Martin
 * asked for — and both dimensions are named in the legend rather than left
 * to be inferred.
 *
 * Issue #379 makes the DETAIL dialog's map an input rather than a picture.
 * While #131's correction form is open on a ground, that map's marker is
 * draggable and a tap on the map moves it — the answer to "nobody knows
 * their ground's latitude, but everybody can see where the pin should be".
 * The position it produces is held here (`correctionPin`) because two
 * children need it — the marker draws from it, the form writes from it —
 * and `pin.via` (`lib/groundEdit.js`) keeps the two directions from chasing
 * each other round.
 *
 * Issue #166 (FAC-06) puts TRAINING FACILITIES on this same map, and the
 * whole difficulty was making them not look like grounds. A ground and a
 * facility are different things — `grounds.place_type` is what a PIN is
 * (#136), `ground_facilities` is what a VENUE IS MADE OF (#165) — and both
 * legitimately sit metres apart, which is why #132's deduper never compares
 * across the two. So a facility gets a marker that differs from a ground's
 * on BOTH channels the ground pins already use and on a third: a smaller
 * ROUND pin (grounds are teardrops), in a violet no ground colour uses, with
 * its SURFACE as the glyph. Nothing about it can be mistaken for "the same
 * ground again".
 *
 * An UNPINNED facility is not drawn at all — it is reported on its venue's
 * popup and card instead. `ground_facilities`'s pin is nullable on purpose
 * ("the venue pin is approximate ... the surface pin is authoritative and
 * drives navigation", schema.ts), so inventing a position for one — a ring
 * around the venue, a jitter — would put a coordinate on the map that no row
 * supports, in the one feature whose entire job is telling somebody where to
 * go. See `lib/facilities.js`.
 *
 * The trust path is the reason `facilitiesByGround` exists rather than the
 * response being rendered directly: a ground is private until verified or
 * published, so a facility must never be a second way to learn one is there.
 * `GET /api/grounds/facilities` answers only for grounds this caller can
 * see, and the client then refuses to draw anything whose ground is not in
 * the directory it already downloaded — two independent gates, because a
 * facility pin is drawn at ACCURATE coordinates and would be a precise
 * announcement of a private venue.
 *
 * Issue #183 adds the street/satellite switcher to BOTH maps on this page.
 * The tiles, the attribution that must travel with them, and the one stored
 * preference the two maps share live in `lib/basemap.js` — including the
 * reason the storage call is over there and not in this file (`nearMe.test.js`
 * asserts this file touches no browser store at all, which is how #134's
 * "the position is never written down" claim is held).
 */

// ---------- Leaflet icon setup ----------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// `size` is 34 everywhere except the one pin a finger has to catch and move
// (issue #379): a marker you only ever tap can be a 34px target, a marker you
// DRAG has to be at least 44 or the drag starts by missing it.
function makeIcon(color, label = "", size = 34) {
  const glyph = Math.max(11, Math.round(size / 3));
  return new L.DivIcon({
    className: "custom-marker",
    html: `<div aria-hidden="true" style="width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;background:${color};border:3px solid #fff;transform:rotate(-45deg);box-shadow:0 6px 14px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">
      <div style="transform:rotate(45deg);color:white;font-weight:900;font-size:${glyph}px;line-height:1;">${label}</div></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size], popupAnchor: [0, 4 - size],
  });
}
// Issue #136: colour = source, glyph = place type. Built once per (colour,
// type) pair at module load rather than per render — `L.DivIcon` instances
// are handed to leaflet, and minting a new one on every render makes leaflet
// tear down and rebuild the marker's DOM node on every unrelated state
// change (a saved bookmark, career stats landing).
const SOURCE_COLORS = { played: "#D4A017", mine: "#3B82F6", global: "#0B2E59" };
const groundIcons = Object.fromEntries(
  Object.entries(SOURCE_COLORS).map(([source, color]) => [
    source,
    Object.fromEntries(
      Object.entries(PLACE_TYPE_META).map(([type, meta]) => [type, makeIcon(color, meta.glyph)]),
    ),
  ]),
);
// The one pin that is not a ground: it is a spot the user just clicked, and
// it has no type yet — that is the question the dialog is about to ask.
const pendingIcon = makeIcon("#DC2626", "+");
// Issue #134: where the device says you are. Drawn so "the map moved to my
// surroundings" is visible rather than inferred — and it is a marker on a
// live map only, never a record of anything.
const youIcon = makeIcon("#0F766E", "•");
// Issue #379: the pin while it is being CORRECTED. Red like the dropped pin
// — both mean "this position is not settled yet" — and 44px, because this is
// the one marker in the app a thumb has to grab and move (the ticket asks
// for 44px touch targets by name).
const MOVABLE_PIN_SIZE = 44;
const movablePinIcon = makeIcon("#DC2626", "+", MOVABLE_PIN_SIZE);

/**
 * Issue #166 — the facility marker: ROUND, SMALLER, VIOLET, surface-glyphed.
 *
 * Every one of those four is doing work. Round vs the grounds' teardrop is
 * the difference that survives a squint and a colour-blind reader; smaller
 * says "part of the venue" rather than "another venue"; violet is outside
 * `SOURCE_COLORS` and outside the red/teal the pending and device markers
 * use, so no existing legend entry gets a second meaning; and the glyph is
 * the SURFACE, which is the fact a player standing in the rain actually
 * wants (T/S/M/C — `lib/facilities.js`).
 *
 * Anchored at its CENTRE, not at its point: a circle has no tip, and
 * anchoring it like a teardrop would draw it half a marker north of where
 * the facility is.
 */
const FACILITY_PIN_SIZE = 26;

/** Only what a DOM attribute can safely carry. Ids and surfaces are
 * server-generated and closed-set respectively, so this is belt-and-braces:
 * this string is interpolated into raw HTML, and "the server would never"
 * is not a property this file can check. */
function attrSafe(value) {
  return String(value ?? "").replace(/[^A-Za-z0-9_-]/g, "");
}

function makeFacilityIcon(f) {
  const size = FACILITY_PIN_SIZE;
  return new L.DivIcon({
    className: "facility-marker",
    html: `<div aria-hidden="true" data-testid="facility-marker-${attrSafe(f.facility_id)}" data-facility-surface="${attrSafe(f.surface)}" style="width:${size}px;height:${size}px;border-radius:50%;background:${FACILITY_COLOR};border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,0.28);display:flex;align-items:center;justify-content:center;">
      <span style="color:white;font-weight:900;font-size:10px;line-height:1;">${attrSafe(facilitySurfaceGlyph(f.surface))}</span></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2],
  });
}

/**
 * Cached by (id, surface) for `groundIcons`'s reason: leaflet is handed the
 * instance, so a fresh `L.DivIcon` on every render tears down and rebuilds
 * the marker's DOM node on every unrelated state change. Keyed on the id
 * BECAUSE the id is in the rendered html (the e2e suite needs to find one
 * marker), and on the surface too so a corrected surface re-mints rather
 * than keeping the old letter.
 */
const facilityIconCache = new Map();
function facilityIcon(f) {
  const key = `${f.facility_id}:${f.surface}`;
  let icon = facilityIconCache.get(key);
  if (!icon) {
    icon = makeFacilityIcon(f);
    facilityIconCache.set(key, icon);
  }
  return icon;
}

/** Issue #134: nothing asked for, nothing known. */
const NEAR_ME_IDLE = { status: "idle", results: [], message: "", position: null };

/**
 * A map can carry more pins than a dropdown can carry rows, so the map asks
 * for more of them than `NEAR_ME_LIMIT`. The RADIUS is the same on both
 * surfaces — that is the number the copy states.
 */
const MAP_NEAR_ME_LIMIT = 25;

/**
 * Issue #643 — THE MAP ASKS FOR WHAT IS ON SCREEN.
 *
 * The defect, in one line: this page fetched the whole directory once, on
 * mount, and never again. Panning to another country changed nothing about
 * what had been downloaded — 11.70 MB and 404 D1 statements at the 20,000
 * grounds epic #609 is judged against, all of it to draw the few dozen pins
 * inside the map's frame.
 *
 * This component is the trigger. It reads the map's real bounds and zoom on
 * `moveend`/`zoomend`, snaps them onto the shared grid (`lib/mapViewport.js`,
 * mirroring `api/src/lib/viewport.ts`) and hands the result up. Everything
 * downstream of that — deduplication, the cache, which contract to expect —
 * belongs to `useDirectoryViewport` and the lib, so this stays four lines of
 * leaflet and a timer.
 *
 * ## Both events, not just one
 *
 * `moveend` fires for a pan and `zoomend` for a zoom, and a scroll-wheel zoom
 * emits BOTH. Binding only `moveend` would miss a zoom that changes the box
 * without moving its centre — which is precisely the gesture that takes a
 * box from more grounds than the row cap to fewer, and switches the whole
 * answer from bubbles to pins.
 *
 * ## The debounce, and the trailing edge
 *
 * `VIEWPORT_DEBOUNCE_MS` carries the derivation. It fires on the TRAILING
 * edge — the map is asked about where the gesture ENDED, never where it
 * started — because a leading-edge fetch would request the viewport the user
 * is already leaving, which is a request guaranteed to be wasted.
 *
 * ## The first paint asks about the world
 *
 * The mount effect below is what replaces the old `useEffect(() => {
 * loadGrounds(); })`. It fires immediately rather than after the debounce —
 * there is no gesture to settle — and it asks `worldViewport(map)` rather
 * than `viewportFromMap(map)`: the whole world, at the mount zoom. The map's
 * opening FRAME is a hard-coded view over north Africa that nobody chose, and
 * asking about it left a New Zealand player with an empty map and an empty
 * card list. The answer is still bounded and still capped — read
 * `worldViewport`'s own header, which carries the cost.
 *
 * After that the map fits itself to what came back (`MapFitter`, as it always
 * did) and every later read is the screen's.
 */
// #1619 (D444): `newLook` is the redrawn map, which mounts on NZ at zoom 5. Its world read is still asked at
// `MAP_MOUNT_ZOOM`, then one ordinary screen read follows (debounced, so a fit's own moveend coalesces with it).
export function ViewportWatcher({ onViewport, newLook = false }) {
  const map = useMap();
  const timer = useRef(null);
  const latest = useRef(onViewport);
  latest.current = onViewport;
  /**
   * The mount fire is the FIRST PAINT and asks about the world; every later
   * fire asks about the screen. Held in a ref rather than inferred, because
   * a remount must not re-ask the world question.
   */
  const firstPaint = useRef(true);

  useEffect(() => {
    // #644 REMOVED THE SEARCH BRIDGE THAT USED TO LIVE ON THIS LINE. While a
    // search was active, #643 made the map ask about the WORLD box instead of
    // its own frame — one cached, capped read that reproduced the pre-viewport
    // reach for any directory small enough to fit inside
    // `GROUNDS_BBOX_MAX_ROWS`, and past it degraded to "zoom in to search
    // here". The search is now a server-side query with no such ceiling
    // (`hooks/useDirectorySearch.js`), so the map is free to go back to asking
    // about exactly one thing: what is on screen.
    const fire = () => latest.current(viewportFromMap(map));
    const schedule = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(fire, VIEWPORT_DEBOUNCE_MS);
    };
    map.on("moveend", schedule);
    map.on("zoomend", schedule);
    // `resize` too, and not for tidiness: the map's bounds are a function of
    // its container's SIZE as well as its centre, so a viewport that changes
    // because the browser was resized (or because #617 reordered this block
    // under a search on a narrow screen) is a new question. Without it the
    // page would hold the answer to a box that is no longer on screen, and
    // no pan would be pending to correct it.
    map.on("resize", schedule);
    // THE FIRST PAINT asks about the WORLD, not about the mount-time frame —
    // see `worldViewport` for why the frame is meaningless before the user
    // has moved the map, and what happened when this asked about it anyway.
    // (At 390px that frame is lat -56..73, lng -29..89: a New Zealand club
    // is not in it, and the page said "No grounds here yet".)
    // Immediately, not in 300 ms: there is no gesture to settle.
    if (firstPaint.current) {
      firstPaint.current = false;
      latest.current(newLook ? worldViewport(map, MAP_MOUNT_ZOOM) : worldViewport(map));
      if (newLook) schedule();
    } else {
      fire();
    }
    return () => {
      map.off("moveend", schedule);
      map.off("zoomend", schedule);
      map.off("resize", schedule);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [map, newLook]);

  return null;
}

/**
 * Issue #642/#643 — A COUNT BUBBLE. When a box holds more grounds than the
 * row cap the server answers with counts per grid cell instead of rows —
 * rather than pinning an arbitrary thousand of them and calling it the map —
 * and this is what that looks like.
 *
 * ## A CELL IS NOT A GROUND, and this component is where that has to hold
 *
 * The API shaped the contract so the client cannot confuse them — a cell
 * carries `{ latitude, longitude, count }` and NO id of any kind, the array
 * is called `cells` and not `grounds`, and the envelope names its own mode —
 * but the last few inches are here. So, deliberately:
 *
 *   - **no `<Popup>`**. A popup is where every ground action lives (open,
 *     save, correct, merge), and a bubble has nothing to put in one: no name,
 *     no city, no surfaces, nothing to bookmark. An empty popup would be an
 *     invitation to fill it.
 *   - **no `onOpen`, no `toggleSaved`, no `setSelected`**. There is no id to
 *     pass to any of them.
 *   - **one gesture: zoom in**, which is the only thing a count can honestly
 *     offer. `cellZoomTarget` says how far.
 *
 * A round bubble in the aggregate colour, not a teardrop: the same argument
 * `lib/facilities.js` makes about facility pins. Shape carries meaning on
 * this map, and a count that looked like a pin would read as a venue with a
 * badge.
 */
const CELL_COLOR = "#0B2E59";

/** The bubble's own diameter — split out of `cellIcon` so the declutter pass
 * below (`declutterCellPositions`) can reason about each bubble's real
 * on-screen radius without re-deriving the text-length rule twice. */
function cellMarkerSize(count) {
  const text = count > 999 ? `${Math.round(count / 1000)}k` : String(count);
  // Bigger for bigger numbers, but only just: a bubble that scaled with the
  // count would put a 20,000 next to a 3 and make the 3 unreadable.
  return text.length > 3 ? 46 : text.length > 2 ? 40 : 34;
}

function cellIcon(count) {
  const text = count > 999 ? `${Math.round(count / 1000)}k` : String(count);
  const size = cellMarkerSize(count);
  return L.divIcon({
    className: "cell-marker",
    html: `<div aria-hidden="true" style="width:${size}px;height:${size}px;border-radius:50%;background:${CELL_COLOR};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:${text.length > 3 ? 11 : 12}px;border:2px solid rgba(255,255,255,0.9);box-shadow:0 1px 4px rgba(0,0,0,0.35)">${text}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * PR #858 review round 3 (B3) — TWO cells that are genuinely close together
 * in the world (an 8°-grid cell in Europe and one just south of it, say)
 * are drawn far enough apart to tell apart on the shipped page's wide map,
 * but project to overlapping pixels on the redrawn screen's 350×140 band —
 * the same geography, far fewer pixels to spread it across. Watched failing
 * live: three stubbed cells 8-16° apart rendered as three 34-46px circles
 * stacked almost exactly on top of each other, so only the TOPMOST one
 * could ever be clicked (`<div>6</div> intercepts pointer events` on the
 * "47" bubble underneath it).
 *
 * This nudges each bubble's RENDERED position, in screen pixels, just far
 * enough that no two circles' edges touch — never touching `cellFit`'s own
 * target (still the cell's real lat/lng, computed from the UN-nudged `c`),
 * so a click still zooms to the true cell. A simple iterative relaxation:
 * a handful of bubbles, a handful of passes, each pass pushing any pair
 * that still overlaps directly apart along the line between their centres.
 * On the shipped page's own wide map this is a no-op for the ordinary case
 * (cells that were already clear of each other stay exactly where they
 * were) — it only ever moves a bubble that would otherwise have covered,
 * and been covered by, another one.
 */
/**
 * PR #858 review round 3 (B3, fourth pass) — the OTHER thing sharing this
 * tiny band's pixels: leaflet's own zoom control (top-left), `Basemaps`'
 * Street/Satellite switcher (bottom-right) and the attribution strip
 * (bottom). A bubble pushed clear of its NEIGHBOURS could still land
 * squarely on one of these — watched failing live, journey 26: a nudged
 * bubble covered the zoom "−" button, so the click meant for the bubble
 * hit the control underneath instead and the map zoomed via the WRONG
 * element (`Expected > 2, Received 1` — the click landed but nothing
 * moved, because `−` doesn't zoom in).
 *
 * Read directly off the DOM (`.leaflet-control-zoom`, `.leaflet-control-
 * layers`, `.leaflet-control-attribution`) rather than hard-coded corners,
 * so this keeps working if any of the three ever moves or resizes — the
 * whole point is "wherever the controls actually are", not "wherever they
 * were measured once".
 */
function controlRects(map) {
  return readControlRects(map.getContainer());
}

/**
 * #917 review, N1 — this docblock used to describe `pushClearOfControls`
 * (moved to `lib/mapFit.js`, whose own header is now the accurate account
 * of that function's rect-avoidance-and-clamp behaviour, including the
 * "not just nearest, the nearest FEASIBLE edge" fix #917 itself made — the
 * copy that sat here still said "to its NEAREST clear edge" unconditionally,
 * which stopped being true the moment that fix landed).
 *
 * What this function actually does, for THIS bubble layer specifically:
 * places every cell at its true map position, then — cell by cell, in the
 * order `cells` arrives in — iteratively moves ONLY THE CURRENT bubble
 * clear of every bubble ALREADY PLACED (the `placed` loop below); an
 * already-placed bubble never moves again once a later one needs room
 * near it. Each pass also asks `pushClearOfControls` to clear the SAME
 * point of leaflet's own controls, repeated until a pass moves nothing, or
 * twelve passes have run (a bound, not a claim that convergence always
 * finishes sooner).
 */
function declutterCellPositions(map, cells) {
  const MIN_GAP_PX = 8;
  const rects = controlRects(map);
  const mapSize = map.getSize();
  const placed = [];
  return cells.map((c) => {
    const r = cellMarkerSize(c.count) / 2;
    let pt = map.latLngToContainerPoint([c.latitude, c.longitude]);
    for (let pass = 0; pass < 12; pass += 1) {
      let moved = false;
      for (const p of placed) {
        const dx = pt.x - p.pt.x;
        const dy = pt.y - p.pt.y;
        const dist = Math.hypot(dx, dy);
        const minDist = r + p.r + MIN_GAP_PX;
        if (dist < minDist) {
          moved = true;
          const push = (minDist - dist) / 2 || minDist / 2;
          // Two cells at the IDENTICAL pixel (dist === 0, e.g. duplicate
          // coordinates) have no direction to push along — pick a fixed
          // one rather than dividing by zero.
          const ux = dist > 0 ? dx / dist : 1;
          const uy = dist > 0 ? dy / dist : 0;
          pt = { x: pt.x + ux * push, y: pt.y + uy * push };
        }
      }
      const cleared = pushClearOfControls(pt, r, rects, mapSize);
      pt = cleared.pt;
      if (cleared.moved) moved = true;
      if (!moved) break;
    }
    placed.push({ pt, r });
    return { cell: c, latlng: map.containerPointToLatLng(pt) };
  });
}

function CellCountMarkers({ cells, declutter }) {
  const map = useMap();
  /**
   * The declutter pass below needs the map's REAL, SETTLED pixel
   * projection — `map.latLngToContainerPoint`, which depends on the
   * container's current size and the view's current centre/zoom. An
   * ordinary React render (this component's own props changing) can land
   * mid-flight: `applyFit`'s own recentring (the mount fit, a search,
   * near-me) updates leaflet's internal transform over several animation
   * frames, and a render that happens to fall inside that window
   * projects against a transform that is about to change again — so the
   * push computed from it is stale the moment it lands. `moveend`/
   * `zoomend`/`resize` are leaflet's own "the view is settled" signals;
   * re-running the declutter pass on those, not just on `cells`
   * changing, is what makes the pushed-apart positions the ones actually
   * on screen rather than a transient in-between.
   *
   * PR #858 review round 3 (B3, third pass) — `declutter` is `compact`,
   * threaded down from `DirectoryMapSurface`. The redrawn screen's own
   * band is the ONLY caller that passes it (true) — the shipped page's
   * map, `60vh` and wide, never needed nudging in the first place, and
   * the reviewer's own diff-confirm proved this pass was moving TWO
   * CELLS 0.3° APART on the shipped page too, which is not this file's
   * to change: `#642`'s bubble is drawn at the mean of the grounds it
   * counts, and moving that pixel on the one map wide enough that it was
   * never colliding with anything would be reporting a location the
   * server never sent. Skipping the whole effect (not merely returning
   * early inside it) when `declutter` is false keeps the shipped leg
   * subscribed to nothing this component didn't already need — every
   * bubble there is exactly where `main` puts it.
   */
  const [, forceRecompute] = useState(0);
  useEffect(() => {
    if (!declutter) return undefined;
    const bump = () => forceRecompute((n) => n + 1);
    map.on("moveend", bump);
    map.on("zoomend", bump);
    map.on("resize", bump);
    return () => {
      map.off("moveend", bump);
      map.off("zoomend", bump);
      map.off("resize", bump);
    };
  }, [map, declutter]);
  if (!cells || cells.length === 0) return null;
  const declustered = declutter
    ? declutterCellPositions(map, cells)
    : cells.map((c) => ({ cell: c, latlng: [c.latitude, c.longitude] }));
  return (
    <>
      {declustered.map(({ cell: c, latlng }) => (
        <Marker
          // No id exists to key on — a cell has none, by design. Its position
          // is unique within one answer (it is the mean of the pins it
          // counts) and the whole layer is replaced on every viewport change.
          key={`cell-${c.latitude},${c.longitude}`}
          position={latlng}
          icon={cellIcon(c.count)}
          // #1176: a focusable bubble needs a name: what it counts.
          title={`${c.count} ${c.count === 1 ? "ground" : "grounds"} in this area`}
          // The ONE gesture a count can honestly offer. Through
          // `applyFit`/`cellFit` rather than a raw leaflet move, so it
          // inherits #212's reduced-motion rule instead of re-deciding it —
          // the same door every other recentring on this page goes through.
          // `c` (the cell's REAL coordinates), not the nudged `latlng` —
          // the declutter pass moves the bubble, never the destination.
          eventHandlers={{ click: () => applyFit(map, cellFit(c, map.getZoom())) }}
        />
      ))}
    </>
  );
}

/**
 * Issue #377 — the marker popup Leaflet opens on a pin click would not
 * dismiss, so a second search was effectively stuck behind it: the reporter
 * (Martin) typed a search, clicked the one matching pin to check it, then
 * tried to refine the search — and the popup just sat there, unmoved by
 * anything they did next, until they reloaded the page.
 *
 * ROOT CAUSE: Leaflet's own popup only closes on ANOTHER click INSIDE THE
 * MAP (`closePopupOnClick`, on by default) or its own [x]. It has no idea
 * the search box, the source/type filters, or the ground cards even exist —
 * they are ordinary DOM siblings outside the map's container, so leaflet's
 * click handling never sees interaction with them, and it binds no
 * `Escape` handling at all. So the ONE way this app offers to close it
 * (click another spot on the map) is not a way a player refining a search
 * in the box above the map has any reason to discover.
 *
 * CHOSEN PRIMARY DISMISSAL: searching again — the exact repro. Typing a new
 * `grounds-search` query closes any open popup, same as picking a result
 * already does implicitly (the matched marker is removed from `filtered`
 * and unmounts, which react-leaflet already tears the bound popup down
 * with). Escape and click-away are required REGARDLESS per the ticket, and
 * neither came for free from leaflet, so both are wired here explicitly:
 * Escape at the document level (a popup takes no keyboard focus of its own
 * to receive it), and click-away as "the pointer went down outside the map
 * container" (a click INSIDE the map is already leaflet's own job and is
 * left alone, so this does not fight `closePopupOnClick`).
 */
function PopupDismissController({ searchTerm }) {
  const map = useMap();
  const lastTerm = useRef(searchTerm);

  useEffect(() => {
    if (searchTerm === lastTerm.current) return;
    lastTerm.current = searchTerm;
    map.closePopup();
  }, [map, searchTerm]);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") map.closePopup(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [map]);

  useEffect(() => {
    const onPointerDown = (e) => {
      const container = map.getContainer();
      if (container && !container.contains(e.target)) map.closePopup();
    };
    // `mousedown`/`touchstart`, not `click`: matches Radix's own
    // outside-dismiss timing (a press outside starts the dismissal, not the
    // eventual click) and — unlike `click` — fires even when the target is
    // removed from the DOM before a click would have (e.g. tapping a
    // ground card that immediately re-renders the list).
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [map]);

  return null;
}

/**
 * Issue #183: publish which basemap is showing, and remember a change to it.
 *
 * Two jobs, one component, because they are the same fact seen from two
 * sides. The *seam* mirrors #212's `data-map-center`/`data-map-zoom`: an e2e
 * suite can then assert the map is ON satellite rather than that a radio
 * input looks checked, which is the same distinction #212 drew between
 * moving the map and intending to move it. The *memory* is leaflet's
 * `baselayerchange` — fired only when leaflet's own control swaps the layer,
 * never when react-leaflet adds one in response to a `checked` prop, so a
 * choice made here cannot loop back through the state that renders it.
 */
// Issue #757 review round 2, B3 — exported (unchanged otherwise) so
// `LogGroundMapBand.jsx` (the step-1 "search the world directory" map band)
// reuses the SAME basemap seam/toggle rather than a second, hard-wired
// implementation. Every other caller on this page is untouched.
export function BasemapSeam({ active, onChoose }) {
  const map = useMapEvents({
    baselayerchange(e) {
      const id = basemapIdFromLabel(e.name);
      if (id) onChoose(id);
    },
  });

  useEffect(() => {
    const el = map.getContainer();
    if (el) el.dataset.mapBasemap = active;
  }, [map, active]);

  return null;
}

/**
 * The street/satellite switcher, identical on both maps on this page.
 *
 * `LayersControl.BaseLayer` rather than a hand-rolled toggle over one
 * `<TileLayer url>`: leaflet owns the attribution control, and it rewrites
 * it when IT swaps the base layer. Swapping the url ourselves would keep
 * OpenStreetMap's credit on screen over Esri's imagery — issue #183 point 2
 * is about exactly that failure.
 *
 * Exactly one entry is `checked`, always: with none, leaflet adds no tile
 * layer at all and the map draws grey.
 *
 * `bottomright`, not leaflet's conventional top corner, because #212's
 * notice card is absolutely positioned across the TOP of this map and at
 * 390px it spans its full width — measured: the card's box is the map's box,
 * 193px tall, and a top-corner control sits inside it, painted over (the
 * card is a later sibling at the same z-index) and unclickable (the card is
 * `pointer-events-auto`). So in exactly the states where the map is empty
 * and switching to imagery is most likely to be what the user wants, a
 * top-corner switcher would not be there. The bottom-right corner holds only
 * the attribution control, which leaflet stacks below this one.
 *
 * `collapsed={false}` — both options on screen rather than behind leaflet's
 * hover-to-open layers icon. Two reasons, one of them a defect:
 *
 *  - Three people asked for satellite over four days on a page that had it
 *    all along, one dialog away. A feature nobody can find is the thing this
 *    issue is actually fixing, and a control that shows its two options is
 *    the difference between "the map has a setting" and "the map has an
 *    icon".
 *  - Collapsed, the panel opens on `mouseenter`, and leaflet sizes it on
 *    open from `map.getSize()`. In the ground-detail dialog that open lands
 *    while radix is still animating the dialog in — reproduced: the pointer
 *    is over where the control WAS, the panel never opens, and on a slower
 *    device it can open clamped to a few pixels of scrollable height.
 *    Expanded at mount, there is no open to mistime.
 */
// Issue #757 review round 2, B3 — exported (unchanged otherwise), same
// reason as `BasemapSeam` just above.
// #1619 (D439) — `wrap` is the new-look maps: the world repeats east and west
// instead of stopping at the date line. Default false keeps #687 for every
// other map.
// #1620 (D439) — `pill` is the new-look maps' one control (`MapControlPill`): no LayersControl, just the chosen
// basemap's tile layer. Keyed by the basemap, so choosing the other one removes the layer and with it its credit
// (#183 point 2: the attribution follows the layer, never the other source's).
export function Basemaps({ active, onChoose, tapBox = false, wrap = false, pill = false }) {
  if (pill) {
    const b = BASEMAPS.find((x) => x.id === active) || BASEMAPS[0];
    return (
      <>
        <WrappingTileLayer key={b.id} attribution={b.attribution} url={b.url} maxZoom={b.maxZoom} />
        <BasemapSeam active={b.id} onChoose={onChoose} />
      </>
    );
  }
  return (
    <>
      {tapBox && <BasemapTapBox />}
      <LayersControl position="bottomright" collapsed={false}>
        {BASEMAPS.map((b) => (
          <LayersControl.BaseLayer key={b.id} name={b.label} checked={b.id === active}>
            {/* #687: `noWrap` — one world, not an infinite horizontal repeat.
                One prop here serves both basemaps and both maps on the page
                (the directory map and the ground-detail dialog map both
                mount `Basemaps`), so it does not need to be set per-map. */}
            {wrap
              ? <WrappingTileLayer attribution={b.attribution} url={b.url} maxZoom={b.maxZoom} />
              : <TileLayer attribution={b.attribution} url={b.url} maxZoom={b.maxZoom} noWrap />}
          </LayersControl.BaseLayer>
        ))}
      </LayersControl>
      <BasemapSeam active={active} onChoose={onChoose} />
    </>
  );
}

/**
 * Issue #547 — the duplicate prompt's third option lands HERE: someone
 * mid-add-a-ground was about to register a second ground at one venue,
 * pressed "add a field or net to this ground instead", and the search
 * dialog closed itself (abandoning the ground draft — its open effect
 * resets every field, and the page's `onOpenChange` clears the seed and
 * pending pin). What remains is to open the EXISTING ground's detail
 * dialog, surfaces in view.
 *
 * `route` is what the dialog calls. The candidate row from
 * `check-duplicate` carries only id/name/city/country/coords, so the full
 * row comes from the directory the page already holds — the two endpoints
 * share one visibility scope (`isGroundVisibleTo`), so a candidate the
 * check named is a row the page has. The guard is for the race (a ground
 * archived or merged between the two reads), and it degrades to words
 * rather than a broken dialog.
 *
 * `arrivalFor` answers gate-aware, AT RENDER, from `canAddFacility` — the
 * same `checkGroundCorrectionGate` mirror as every facility write — so a
 * directory refresh that changes the answer can never leave a stale
 * invitation pointing at a form the server would refuse. Whoever passes
 * gets #543/#548's add form already open with the carried pin placed
 * (`autoAdd`); everyone else gets `SURFACE_INSTEAD_NOTE`, the honest
 * sentence, because a routed-to form the server would 403 is the control
 * this codebase refuses to render (#271). Only the FACTS — which ground,
 * which pin — are held as state.
 */
function useSurfaceInstead(enriched, user, setSelected, setCardGround, isPlayer) {
  const [invite, setInvite] = useState(null); // { groundId, pin } | null
  // #1075 — the player screen has no `selected` dialog: it opens the ground
  // CARD, which resolves the ground by id (so no `enriched` lookup, and no
  // "find it in the directory" toast: the card is not capped to the list).
  const cardLook = newUi("grounds") && newUi("ground-card") && isPlayer;
  const route = useCallback((candidate, pin) => {
    const target = cardLook ? candidate : enriched.find((x) => x.ground_id === candidate.ground_id);
    if (!target) {
      toast.error(`Couldn't open "${candidate.name}" — find it in the directory to add its fields and nets.`);
      return;
    }
    (cardLook ? setCardGround : setSelected)(target);
    setInvite({ groundId: target.ground_id, pin: pin ?? null });
  }, [enriched, setSelected, setCardGround, cardLook]);
  const clear = useCallback(() => setInvite(null), []);
  const arrivalFor = useCallback((ground, correcting) => {
    if (!invite || !ground || invite.groundId !== ground.ground_id) return { note: false, autoAdd: null };
    if (!canAddFacility(ground, user)) return { note: true, autoAdd: null };
    return { note: false, autoAdd: correcting ? null : { pin: invite.pin } };
  }, [invite, user]);
  return { route, clear, arrivalFor };
}

/** #547 — the ineligible arrival's line, rendered where the surfaces are. */
function SurfaceInsteadNote({ ground }) {
  return (
    <p
      className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600"
      data-testid={`surface-instead-note-${ground.ground_id}`}
    >
      {SURFACE_INSTEAD_NOTE}
    </p>
  );
}

/**
 * Issue #608 — the directory failed to load, said in the list's own zero slot.
 *
 * A module-level component rather than an inline block, for two reasons: the
 * page function is already at its complexity budget, and a failure panel is
 * exactly the kind of markup that gets quietly deleted as "duplicate" of the
 * empty state it exists to displace.
 */
function DirectoryLoadError({ onRetry }) {
  return (
    <div className="col-span-3 text-center py-8" data-testid="grounds-directory-error" role="alert">
      <TriangleAlert size={20} className="mx-auto mb-2 text-rose-500" />
      <div className="font-display font-bold text-sm text-slate-900">{DIRECTORY_ERROR_TITLE}</div>
      <p className="text-xs text-slate-500 mt-1">{DIRECTORY_ERROR_BODY}</p>
      <Button size="sm" variant="outline" className="mt-3 h-8" onClick={onRetry} data-testid="grounds-directory-error-retry">
        <RotateCw size={14} className="mr-2 shrink-0" />
        {DIRECTORY_RETRY_LABEL}
      </Button>
    </div>
  );
}

/**
 * #608: the surfaces layer failed on its own. The grounds are real and drawn;
 * only the nets and pitches are missing, and saying which is what stops "this
 * venue has no nets" from being read off a request that never arrived.
 */
function FacilitiesLoadError({ failed, directoryFailed }) {
  // Silent when the directory itself is down: two stacked failure panels for
  // one outage is noise, and the directory's is the one that matters.
  if (!failed || directoryFailed) return null;
  return (
    <p className="px-4 py-2 text-xs text-rose-600 border-t border-slate-200" data-testid="grounds-map-facilities-error">
      Pitches and nets couldn&apos;t be loaded, so they aren&apos;t shown on the map or the cards. The grounds themselves are fine.
    </p>
  );
}

/**
 * Issue #643 — WHAT THE SERVER LEFT OUT, SAID ON SCREEN.
 *
 * #609's fifth requirement is that degradation is VISIBLE. The viewport read
 * caps at 1,000 rows (500 cells) and reports `truncated`; a client that took
 * the rows and ignored the flag would draw an arbitrary 1,000 of 20,000 pins
 * as though they were the whole answer, and nothing on the map could tell a
 * user otherwise. That is the same defect class as #617's "All grounds (1)"
 * and #612's per-page counts — a number that is quietly wrong.
 *
 * The sentence itself is `viewportTruncationNotice` in `lib/mapViewport.js`,
 * where it can be tested; this is where it is shown. At the BOTTOM of the
 * map, so it never sits on top of #212's notice panel: they can both be true
 * at once ("nothing matched your search" and "and there is more out here
 * than fitted").
 */
function MapTruncationNotice({ text }) {
  if (!text) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-[1000] flex justify-center p-3"
      data-testid="grounds-map-truncated"
      role="status"
      aria-live="polite"
    >
      <span className="rounded-full bg-slate-900/85 px-3 py-1.5 text-[11px] font-semibold text-white shadow">
        {text}
      </span>
    </div>
  );
}

/**
 * The card grid's zero line — except it is no longer only shown at zero.
 *
 * #608 established the first split: a FAILED fetch owns this slot when there
 * is one, because the filter line ("try changing the filter") is just as
 * wrong on a 500 as the map's was. #643 adds the third — below the zoom
 * threshold there are no rows to list at all, and telling somebody looking at
 * bubbles counting twenty thousand grounds to "try changing the filter" is
 * #608's defect wearing a different hat. #644 adds the fourth: a search whose
 * answer has not arrived, which is empty for a reason that is neither a
 * failure nor an emptiness.
 *
 * #688 adds a fifth line that is NOT an empty-state at all: `outcome ===
 * "aggregated-partial"` fires with `count > 0`, after the grid has already
 * rendered a player's own saved/played cards above this component — it
 * exists so the map's zoomed-out bubbles are not silently mistaken for "the
 * world holds only what's listed here".
 *
 * Module-level rather than inline in the grid for the reason every other
 * component on this page is: `GroundsMap` is at its `max-lines-per-function`
 * budget, and two nested ternaries in JSX are two branches that count against
 * its complexity as well.
 */
function DirectoryGridEmpty({ directoryError, mode, count, searching, pending, onRetry }) {
  const outcome = directoryOutcome({ failed: directoryError, count, mode, searching, pending });
  if (outcome === "failed") return <DirectoryLoadError onRetry={onRetry} />;
  if (outcome === "list") return null;
  // #644: a fourth reason for this slot to be empty, with a fourth remedy —
  // wait. The search runs on the server, so between the keystroke and the
  // answer there is a moment with a term and no rows, and "No grounds match"
  // there is a wrong answer rather than a slow one.
  if (outcome === "pending") {
    return (
      <div className="col-span-3 text-center text-slate-500 py-8" data-testid="grounds-directory-searching" role="status">
        Searching the directory…
      </div>
    );
  }
  if (outcome === "aggregated") {
    return (
      <div className="col-span-3 text-center text-slate-500 py-8" data-testid="grounds-directory-aggregated">
        Zoom in to list the grounds here — the map is showing how many are in each area.
      </div>
    );
  }
  // #688: the cards ABOVE this line are already rendered — this fires
  // alongside them, not instead of them (`outcome` is computed off the same
  // `count` this component is handed, and `directoryOutcome` only reaches
  // this branch when that count is > 0). Without it, a player with a
  // handful of saved/played grounds at world zoom would see their own cards
  // and nothing to say the bubbles behind them hold thousands more — silently
  // implying the world holds only what's listed.
  if (outcome === "aggregated-partial") {
    return (
      <div className="col-span-3 text-center text-xs text-slate-500 pt-1 pb-4" data-testid="grounds-directory-zoom-hint" role="status">
        That's your grounds — zoom in to list everything else here.
      </div>
    );
  }
  return (
    <div className="col-span-3 text-center text-slate-500 py-8">
      No grounds match — try changing the filter or adding one.
    </div>
  );
}

/**
 * Issue #134 — WHAT "USE MY LOCATION" DID, said once, above the map it just
 * moved.
 *
 * Two mutually exclusive states and nothing else: the plain failure line, and
 * the banner that reports the ranking. The banner's second half is the offer
 * to ADD this place when nothing was in range, and the note beside it is
 * there because the two presses are two different consent events — ranking
 * sends nothing anywhere, adding creates a public record at these coordinates
 * and asks OpenStreetMap what town they are in. That distinction has to be
 * legible at the point of the SECOND press, not inferred from the first one's
 * reassurance (#134's adversarial review).
 *
 * Module-level since #644, for this file's usual reason and its usual cause:
 * `GroundsMap` is measured against `max-lines-per-function` (710) and
 * `complexity` (65), the search rewiring pushed it past both, and the budget
 * exists precisely to say so. Its own header forbids raising a number to make
 * new code fit — so the code moves instead. Nothing here changed but the
 * indentation and the two prop names.
 */
function NearMeBanner({ nearMe, results, onAddHere, onClear }) {
  return (
    <>
      {nearMe.status === "error" && (
        <p className="text-xs text-slate-500" role="status" aria-live="polite" data-testid="grounds-near-me-message">
          {nearMe.message}
        </p>
      )}
      {nearMe.status === "ready" && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
          role="status"
          aria-live="polite"
          data-testid="grounds-near-me-banner"
        >
          <LocateFixed size={14} className="shrink-0" style={{ color: "#0F766E" }} />
          <div className="min-w-0 flex-1">
            <span className="text-xs text-slate-600">
              {results.length > 0
                ? `Nearest first — ${results.length} ground${results.length === 1 ? "" : "s"} within ${NEAR_ME_RADIUS_KM} km of you.`
                : `No known grounds within ${NEAR_ME_RADIUS_KM} km of you.`}
            </span>
            {/*
              Issue #134, adversarial review. Ranking the list sent nothing
              anywhere — the marker on the map says exactly that. Adding a
              ground here does something different: it creates a public
              record at these coordinates and asks OpenStreetMap what town
              they are in, on open, before any save. The two consent events
              have to be distinguishable at the point of the second press,
              not inferred from the first one's reassurance.
            */}
            {results.length === 0 && (
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="grounds-near-me-add-note">
                We&apos;ll look up the town and country for these coordinates.
              </p>
            )}
          </div>
          {results.length === 0 && (
            <Button
              size="sm"
              className="min-h-11 text-white"
              style={{ background: "#0B2E59" }}
              onClick={onAddHere}
              data-testid="grounds-near-me-add"
            >
              <MapPinPlus size={14} className="mr-2 shrink-0" />
              <span className="truncate">Add this place</span>
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="min-h-11 ml-auto text-slate-600"
            onClick={onClear}
            data-testid="grounds-near-me-clear"
          >
            <X size={14} className="mr-1.5 shrink-0" /> Clear
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * Issue #644 — THE WAY TO THE REST OF THE ANSWER.
 *
 * Offered only when there IS a rest: one page is not a paged list, and a
 * disabled pair of arrows under every search would be furniture on a page
 * whose whole problem (#617) was furniture between the question and the
 * answer.
 *
 * It says which page of how many, because "Next" alone leaves a reader with
 * no idea whether they are near the end — and because the count line above
 * the grid states a range, and a range without a page number is ambiguous the
 * moment two pages are the same size.
 *
 * Module-level for this file's usual reason: `GroundsMap` is measured against
 * `max-lines-per-function`, and every branch in JSX counts against its
 * complexity as well.
 */
function DirectoryPager({ listing, onGoTo }) {
  if (!listing || listing.pageCount <= 1) return null;
  const { page, pageCount } = listing;
  return (
    <div className="flex items-center justify-center gap-3" data-testid="grounds-directory-pager">
      <Button
        variant="outline"
        size="sm"
        className="min-h-11"
        disabled={page <= 1}
        onClick={() => onGoTo(page - 1)}
        data-testid="grounds-directory-prev"
      >
        Previous
      </Button>
      <span className="text-xs text-slate-500" data-testid="grounds-directory-page">
        Page {page} of {pageCount}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="min-h-11"
        disabled={page >= pageCount}
        onClick={() => onGoTo(page + 1)}
        data-testid="grounds-directory-next"
      >
        Next
      </Button>
    </div>
  );
}

/**
 * The card inside the map's notice overlay.
 *
 * Module-level since #608: the page function is at its line budget, and this
 * panel now has three mutually exclusive affordances (add, retry, clear
 * filters) rather than one. The POSITIONED WRAPPER stays in the page, inside
 * the map container — #212's whole point is that the message appears where the
 * eye is, and a component that could be dropped anywhere would lose that.
 */
/**
 * Issue #765 / PR #858 F5 — the `grounds-map-notice` overlay, called
 * identically by the shipped page's own return AND the redrawn screen's
 * branch (`GroundsMap()`'s own complexity budget is what pushed this out
 * to a function rather than repeating the same two ternaries inline in
 * both places).
 */
function DirectoryMapNoticeOverlay({ mapNotice, onAdd, onRetry, clearFiltersOffer, compact }) {
  if (!mapNotice) return null;
  return (
    <div
      // Compact: `justify-end` (top-RIGHT), not `justify-start` — the
      // band's own top-LEFT corner is leaflet's own zoom control
      // (`.leaflet-top.leaflet-left`) and bottom-right is the attribution
      // + basemap switcher (`Basemaps`, `position="bottomright"`); a
      // top-left notice sat on top of "Zoom in", intercepting its clicks
      // (watched failing live, journey 26's own zoom-in step, this
      // review round's own mutation test for B3). Top-right is the one
      // corner nothing else in this band claims.
      className={`pointer-events-none absolute inset-x-0 top-0 z-[1000] flex p-2 ${compact ? "justify-end" : "justify-center p-3"}`}
      // #1193: the compact (redrawn) band's top-right now holds the
      // 44px full-screen button; keep the notice clear of it (8 + 44 + 8).
      style={compact ? { paddingRight: 60 } : undefined}
      data-testid="grounds-map-notice"
      role="status"
      aria-live="polite"
    >
      <MapNoticePanel
        notice={mapNotice}
        onAdd={onAdd}
        onRetry={onRetry}
        clearFilters={mapNotice.kind === "no-matches" ? clearFiltersOffer : null}
        compact={compact}
      />
    </div>
  );
}

/**
 * PR #858 review round 3 (B3) — the redrawn screen's own map band is 140px
 * tall (`grounds-near-map`, `GroundsNearYouScreen.jsx`), a fraction of the
 * shipped page's `60vh` map. `MapNoticePanel`'s full card — icon, title,
 * a full body paragraph, a full-width CTA row — was sized for that bigger
 * surface; reused verbatim inside the small band it filled (and in the
 * strip capture, overran) the entire thing, leaving no way to pan, zoom or
 * tap a marker underneath it (watched failing live, `band-strip.png`).
 *
 * `compact` swaps in a single-line pill instead: icon, title, one inline
 * text action — no body paragraph, no full-width button, capped to a
 * fraction of the band's own width so the rest of the map stays visible
 * and, because the wrapper above is `pointer-events-none` outside this
 * pill's own bounding box, reachable. The shipped page never passes
 * `compact`, so its own card (checked by name in `GroundsMap.test.js`) is
 * byte-identical to before this fix.
 */
function CompactMapNoticePanel({ notice, onAdd, onRetry }) {
  // PR #858 review round 3 (B3, second pass) — measured live at 390px:
  // the two-line card (icon+title, then an action on its own line below)
  // was ~58px tall, which reached past where `Basemaps`' own
  // `position="bottomright"` switcher starts (~54px down this 140px
  // band) — covering it, and blocking its clicks, exactly what the first
  // compact pass (top-left → top-right, clearing leaflet's OWN zoom
  // control) did not catch because that measurement never checked the
  // OTHER control sharing this same tiny band.
  //
  // ONE LINE fixes the height (icon, a truncated title, the action, all
  // inline — under half the two-line version's own height, well clear of
  // the switcher). `pointer-events: none` on the card itself is the
  // belt-and-braces the switcher still needs even so: only the action
  // TEXT is `pointer-events-auto`, so a drag or a tap that lands on the
  // card's own padding — not the word itself — reaches the map (or the
  // switcher) underneath rather than being swallowed by a card whose job
  // is to report one line, not to claim the band.
  const kind = notice.kind === "load-failed" ? "retry" : notice.cta ? "add" : null;
  const actionLabel = kind === "retry" ? DIRECTORY_RETRY_LABEL : notice.cta;
  const onAction = kind === "retry" ? onRetry : onAdd;
  return (
    <div className="pointer-events-none flex max-w-[190px] items-center gap-1.5 rounded-full border border-slate-200 bg-white/95 px-2 py-1 shadow">
      <MapNoticeIcon kind={notice.kind} />
      <span className="min-w-0 truncate text-[11px] font-semibold text-slate-900">{notice.title}</span>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          data-testid={kind === "retry" ? "grounds-map-notice-retry" : "grounds-map-notice-add"}
          className="pointer-events-auto shrink-0 text-[11px] font-semibold text-[#0B2E59] underline"
          // D221 G4: a 44px hit area; the drawn pill and its text do not move (#1288 surfaced it for a coach).
          style={TAP_LINK_STYLE}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function MapNoticePanel({ notice, onAdd, onRetry, clearFilters, compact }) {
  if (compact) return <CompactMapNoticePanel notice={notice} onAdd={onAdd} onRetry={onRetry} />;
  return (
    <div className="pointer-events-auto w-full max-w-md rounded-lg border border-slate-200 bg-white/95 shadow-lg backdrop-blur-sm px-4 py-3">
      <div className="flex items-start gap-2.5">
        <MapNoticeIcon kind={notice.kind} />
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-slate-900 break-words">{notice.title}</div>
          <p className="text-xs text-slate-500 mt-0.5">{notice.body}</p>
          {notice.cta && (
            <Button
              size="sm"
              className="mt-2.5 h-8 w-full sm:w-auto text-white"
              style={{ background: "#0B2E59" }}
              onClick={onAdd}
              data-testid="grounds-map-notice-add"
            >
              <MapPinPlus size={14} className="mr-2 shrink-0" />
              <span className="truncate">{notice.cta}</span>
            </Button>
          )}
          {/* #608: the retry affordance. Both reads are re-issued together —
              the surfaces layer hangs off the same page load and is usually
              down for the same reason. */}
          {notice.kind === "load-failed" && (
            <Button
              size="sm"
              className="mt-2.5 h-8 w-full sm:w-auto text-white"
              style={{ background: "#0B2E59" }}
              onClick={onRetry}
              data-testid="grounds-map-notice-retry"
            >
              <RotateCw size={14} className="mr-2 shrink-0" />
              <span className="truncate">{DIRECTORY_RETRY_LABEL}</span>
            </Button>
          )}
          {/* The chips and the search compose, so the zero state has to name
              which of the two emptied the map. */}
          {clearFilters && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 sm:mt-2.5 sm:ml-2 h-8 w-full sm:w-auto"
              onClick={clearFilters.onClear}
              data-testid="grounds-map-notice-clear-filter"
            >
              <span className="truncate">Show all {clearFilters.count} match{clearFilters.count === 1 ? "" : "es"}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** #608: a failure wears a different glyph from a search miss. */
function MapNoticeIcon({ kind }) {
  if (kind === "load-failed") return <TriangleAlert size={16} className="mt-0.5 shrink-0 text-rose-500" />;
  return <SearchX size={16} className="mt-0.5 shrink-0 text-slate-400" />;
}

/**
 * Issue #617 — the search's answer, directly under the box that asked it.
 *
 * Martin reported twice in five minutes that searching a ground "doesn't
 * appear as a match, the only thing that happens is the map zooms in". It
 * always did match: the card was rendered in `grounds-directory-grid`, below a
 * 60vh map, below a header, a filter card and three legend rows — off the
 * bottom of a 1184px laptop, never mind a phone. This line is the fix's first
 * half: the count and the names, above the map, at every width.
 *
 * A BUTTON, not a paragraph — pressing it scrolls the matched cards into
 * view, so the sentence is also the way to what it describes. Returns `null`
 * only for an EMPTY box: a search that found nothing renders the zero form
 * below, carrying both remedies. See `lib/searchResultSummary.js` for why
 * that case moved here from the map notice.
 */
function SearchResultLine({ summary, onJump, onSearchWorldwide, clearFilters, tapBox = false }) {
  if (!summary) return null;

  // The zero answer, in the fold. Journey 1b measured the map's own version of
  // this sentence ending at 1067px on an 844px phone — 224px below the fold,
  // for exactly the reason this issue exists. So it is said here TOO. The map
  // keeps its own copy (#212, and Journey 1 asserts it): that one is for the
  // reader who has scrolled to the map and finds it empty, and this one is for
  // the reader who has not scrolled at all.
  if (summary.kind === "none") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="grounds-search-summary"
        className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
      >
        <div className="flex items-start gap-2">
          <SearchX size={14} className="mt-0.5 shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <span className="text-xs text-slate-600">
              <span className="font-semibold text-slate-900">{summary.headline}</span>
              {" — "}
              <span className="break-words">{summary.detail}</span>
            </span>
            {/* Both remedies for an empty result, beside the sentence that
                reports it. The worldwide search is the one that usually
                applies (the ground exists, it just is not in the directory
                yet); the filter escape only appears when a chip is in fact
                narrowing, because a control that never changes anything is
                noise — the same rule the filter bar follows. */}
            <div className="mt-2 flex flex-wrap gap-2">
              {tapBox ? (
                /* #1394 (D221 G4, D354): the redrawn screen's call only. The 32px navy button is drawn
                   inside a transparent 44px tap box; the shipped page below keeps its `Button`. */
                <TapBoxButton
                  drawnHeight="2rem"
                  drawnClassName={`${buttonVariants({ size: "sm" })} h-8 min-w-0 max-w-full text-white`}
                  drawnStyle={{ background: "#0B2E59" }}
                  style={{ flexShrink: 1, minWidth: 0, maxWidth: "100%" }}
                  onClick={onSearchWorldwide}
                  data-testid="grounds-search-summary-worldwide"
                >
                  <MapPinPlus size={14} className="mr-2 shrink-0" />
                  <span className="truncate">Search worldwide for “{summary.term}”</span>
                </TapBoxButton>
              ) : (
                <Button
                  size="sm"
                  className="h-8 min-w-0 text-white"
                  style={{ background: "#0B2E59" }}
                  onClick={onSearchWorldwide}
                  data-testid="grounds-search-summary-worldwide"
                >
                  <MapPinPlus size={14} className="mr-2 shrink-0" />
                  <span className="truncate">Search worldwide for “{summary.term}”</span>
                </Button>
              )}
              {clearFilters && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 min-w-0"
                  onClick={clearFilters.onClear}
                  data-testid="grounds-search-summary-clear-filter"
                >
                  <span className="truncate">
                    Show all {clearFilters.count} match{clearFilters.count === 1 ? "" : "es"}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PR #858 review round 3 (B4) — `onJump` is nullable now: the redrawn
  // screen (`GroundsMap()`'s own branch below) has no `grounds-directory-
  // grid` for a jump to land in — that grid is the shipped page's own,
  // and this screen's only "list" is the played one, which this line's own
  // count (the WORLD directory's matches, not the played list's) has no
  // business claiming to reveal a row in. It used to pass a no-op instead,
  // which rendered "Show in list" as a button that visibly did nothing —
  // watched failing live. Without a place to jump to, this renders the
  // same count as a plain status line and drops the "Show in list"
  // affordance entirely, rather than promising a jump nothing performs.
  if (!onJump) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="grounds-search-summary"
        className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
      >
        <Search size={14} className="shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1 text-xs text-slate-600">
          <span className="font-semibold text-slate-900">{summary.headline}</span>
          {" — "}
          <span className="break-words">{summary.detail}</span>
        </span>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" data-testid="grounds-search-summary">
      <button
        type="button"
        onClick={onJump}
        className="flex w-full min-h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-[#0B2E59]"
        data-testid="grounds-search-summary-jump"
      >
        <Search size={14} className="shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1 text-xs text-slate-600">
          <span className="font-semibold text-slate-900">{summary.headline}</span>
          {" — "}
          <span className="break-words">{summary.detail}</span>
        </span>
        <span className="shrink-0 text-[11px] font-semibold text-[#0B2E59]">Show in list</span>
      </button>
    </div>
  );
}

/**
 * The map's own furniture — the drop-a-pin hint and the three legends.
 *
 * ONE block rather than four page-level siblings (#617). Two reasons, both
 * structural: it travels with the map when a search pushes the map below the
 * results on a narrow screen (`className` carries that `order`), and it takes
 * ~40 lines out of `GroundsMap`, which was two lines under its
 * `max-lines-per-function` budget before this issue added anything.
 *
 * The rows are `gap-2.5` internally where they were `space-y-6` siblings: this
 * is vertical furniture between the search box and the answer, and every pixel
 * of it was part of why the answer was below the fold.
 */
function DirectoryLegend({ isPlayer, facilitiesTotal, className }) {
  return (
    <div className={`flex flex-col gap-2.5 ${className}`} data-testid="grounds-map-legends">
      <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="grounds-map-hint">
        <MapPinPlus size={12} color="#DC2626" />
        Tip: click anywhere on the map to drop a pin and add that spot as a ground.
      </p>

      {/* Legend. Two rows because the pins carry two facts (issue #136):
          colour says whose ground it is, the letter says what kind of place
          it is. Naming both beats leaving the second to be guessed. */}
      <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
        {isPlayer && <LegendChip color="#D4A017" label="Where you've played" />}
        <LegendChip color="#3B82F6" label="Your ground / saved" />
        <LegendChip color="#0B2E59" label="Global directory" />
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] text-slate-600" data-testid="grounds-type-legend">
        {Object.entries(PLACE_TYPE_META).map(([type, meta]) => (
          <div key={type} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-slate-700 text-white text-[9px] font-black">
              {meta.glyph}
            </span>
            <span>{meta.label}</span>
          </div>
        ))}
      </div>
      {/* Issue #166. A THIRD row, not a fourth chip on the second one: the
          round violet pin is not another kind of ground, and putting it
          beside F/N/I would say it was. The sentence is what stops a reader
          seeing a nets FACILITY beside its club and reading it as the same
          club pinned twice — which is exactly the confusion #132's deduper
          refuses to resolve, because they are genuinely different records. */}
      {facilitiesTotal > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600" data-testid="grounds-facility-legend">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1">
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[9px] font-black"
              style={{ background: FACILITY_COLOR }}
            >
              T
            </span>
            <span>Field or net (round pin) — a surface AT a venue, not a venue of its own</span>
          </span>
          <span className="text-slate-400">Letter = surface: T turf, S synthetic, M matting, C concrete</span>
        </div>
      )}
    </div>
  );
}

/**
 * Issue #765 / PR #858 review round 1 (F3) — back to ONE component, not a
 * gate mounting a separate one. The reviewer's own instruction was to
 * REUSE the shipped map (its fit, its viewport-bound fetch, its facilities
 * layer, its attribution, its marker taps) rather than rebuild a second
 * one — and that map's own JSX closes over a couple of dozen values this
 * component already computes (`fit`, `mappable`, `facilityPins`,
 * `directoryBasemap`, the ground-detail `selected` state, `enriched`,
 * …). Splitting the redrawn screen into a separate component (the
 * original round of this file) meant either duplicating all of that or
 * threading it through as props two levels deep; sharing this component's
 * own closure is what "reuse it, don't rebuild it" actually means here.
 * `newUi("grounds") && isPlayer` (F6) is checked once, after every hook
 * above has run (Rules of Hooks) and every value the redrawn screen or the
 * shipped map JSX needs has been computed — coaches and admins always get
 * the branch below, unconditionally on the flag.
 */
/**
 * Issue #766, D167 G1/G3/G12 — the flag-on PLAYER branch of `/grounds-map`,
 * extracted out of `GroundsMap` itself (PR #921 review, F4 — see that
 * function's own comment at its one call site for why). Owns exactly two
 * decisions of its own: which of `GroundCardScreen`/`GroundsNearYouScreen`
 * is showing, and whether a row's tap has anywhere to send its ground at
 * all — everything else is `GroundsMap`'s own state and handlers, passed
 * straight through as props.
 *
 * G12 — the card REPLACES the list, the same "one route, two
 * compositions, never both in the DOM" shape #765 itself used to replace
 * the shipped page: while a ground is open, this returns `GroundCardScreen`
 * and NOTHING else (no `GroundsNearYouScreen`, so its own `ground-card-*`-
 * prefixed rows — there are none, #765 built none — and this card's own
 * `ground-card-*` ids can never appear beside each other, let alone
 * beside the shipped directory's `ground-card-${id}` cards, which this
 * whole component never renders either).
 *
 * PR #921 review (F4) — `onOpenGround` is gated on `newUi("ground-card")`
 * HERE, explicitly, rather than passed through unconditionally: with that
 * flag off, a row still became `role="button"`/`tabIndex={0}`/
 * `cursor: pointer` and did nothing on tap — a dead, misleading control,
 * not the "harmless" no-op the previous version's own comment claimed.
 */
function GroundsPlayerScreen({
  audience = "player",
  selectedCardGround,
  onCardBack,
  savedIds,
  onToggleSaved,
  groundsNearYou,
  onOpenGround,
  term,
  searchText,
  setTerm,
  searchPending,
  searchSummary,
  onSearchWorldwide,
  clearFiltersOffer,
  viewChips,
  mapNotice,
  onRetryDirectory,
  directoryBasemap,
  chooseBasemap,
  fit,
  signature,
  fitReason,
  holdFirstFit,
  onViewportChange,
  searching,
  cells,
  q,
  onMapDrop,
  pendingPin,
  mappable,
  iconFor,
  facilitiesForGround,
  facilityPins,
  addGroundOpen,
  onAddGroundOpenChange,
  addGroundSeed,
  onGroundAdded,
  onUseExistingGround,
  onAddSurfaceInstead,
  arrivalFor,
  onArrivalConsumed,
}) {
  // #1430 (D386): from the laptop tier the opened ground's card is the detail column's second block, under the
  // map, and the list stays beside it; below it the card replaces the list, as G12 says.
  const laptop = useLaptop();
  // Crossing the tier with a card open swaps the full-page card for the embedded one: the full-page card's
  // cleanup hands the page back to Layout, and the embedded one (which owns nothing) must not leave it so.
  const { setActive: setChromeless } = useChromelessOverride();
  useEffect(() => {
    if (laptop) setChromeless(true);
  }, [laptop, selectedCardGround, setChromeless]);
  const cardScreen = (embedded) => (
    <GroundCardScreen
      groundId={selectedCardGround.ground_id}
      hint={selectedCardGround}
      savedIds={savedIds}
      onToggleSaved={onToggleSaved}
      onBack={onCardBack}
      embedded={embedded}
      // #1075 — the "add a field or net instead" arrival, held by
      // `useSurfaceInstead`; the card asks it, and tells it when it closes.
      arrivalFor={arrivalFor}
      onArrivalConsumed={onArrivalConsumed}
      // Issue #1008 (D203.1) — hide Indoor on the card's own "Correct
      // details" form. Read HERE, at the gate that already decided this
      // component mounts at all, not inside `GroundCard`/`GroundEditPanel`
      // themselves — those are shared with `AdminVenues.jsx`, which has
      // no flag of its own and must stay byte-identical.
      hideIndoor={newUi("ground-card")}
    />
  );
  if (newUi("ground-card") && selectedCardGround && !laptop) return cardScreen(false);
  // #1620: the map's locate button does what "Use my location" does (named so the hooks rule sees a plain function).
  const { useMyLocation: nearMeAction } = groundsNearYou;
  const locate = () => {
    track("grounds.near_me");
    nearMeAction();
  };
  return (
    <>
      <GroundsNearYouScreen
        {...groundsNearYou}
        audience={audience}
        onOpenGround={newUi("ground-card") ? onOpenGround : undefined}
        detailCard={newUi("ground-card") && selectedCardGround ? cardScreen(true) : null}
        onGroundAdded={onGroundAdded}
        onUseExistingGround={onUseExistingGround}
        // #1220 (D284, D285) — Search worldwide and a dropped pin open the
        // new-look add panel, not `GroundSearchDialog` (which the coach and
        // admin page below still uses). The host keeps the open/seed/pin
        // state because the map and the search line live here.
        addRequest={addGroundOpen ? { seed: addGroundSeed, pin: pendingPin } : null}
        onAddRequestClose={() => onAddGroundOpenChange(false)}
        onAddSurfaceInstead={onAddSurfaceInstead}
        viewChips={viewChips}
        term={term}
        // #1405 — what the field shows is what was typed, spaces and all;
        // `term` above is the trimmed copy the filters and the server read.
        searchText={searchText}
        setTerm={setTerm}
        searchPending={searchPending}
        // Issue #765 / PR #858 F5 — the EXACT shipped #617 component,
        // pre-rendered rather than re-implemented: "N matches" (a
        // positive search) and "No matches" both come from here now,
        // byte-identical to the shipped page's own behaviour, including
        // the worldwide/clear-filter remedies. PR #858 review round 3
        // (B4): `onJump` has nothing meaningful to scroll to on this
        // screen (no `grounds-directory-grid`) — passing a no-op used to
        // render "Show in list" as a button that visibly did nothing on
        // press, watched failing live. `null` tells `SearchResultLine`
        // there is no list to jump to, so it renders the same match
        // count without that dead affordance.
        // #1288 (D367.9): the coach's no-match answer is the list's own
        // "Nothing matches …" card, and the board draws no worldwide search
        // for a coach, so the directory's "No matches … Search worldwide"
        // line is dropped on the coach leg only. A positive search keeps its
        // count, and the player's leg is unchanged.
        searchResultLine={
          audience === "coach" && searchSummary?.kind === "none" ? null : (
            <SearchResultLine
              summary={searchSummary}
              onJump={null}
              onSearchWorldwide={onSearchWorldwide}
              clearFilters={clearFiltersOffer}
              tapBox
            />
          )
        }
        // The SAME `grounds-map-notice` overlay the shipped page's own
        // map wears — "grounds-map-notice*" family, F5's other ask.
        // PR #1273 F1: Add opens a dialog, so it leaves full screen first
        // (the same `closeThen` Log a match's pin pick uses).
        // #1288 (D367.9a): a coach's no-match search shows no worldwide
        // search anywhere, so the overlay's "Search worldwide" call to
        // action is not drawn for a coach in that state.
        mapNoticeElement={({ closeThen }) =>
          audience === "coach" && term && mapNotice?.kind === "no-matches" ? null : (
            <DirectoryMapNoticeOverlay
              mapNotice={mapNotice}
              onAdd={() => closeThen(onSearchWorldwide)}
              onRetry={onRetryDirectory}
              clearFiltersOffer={clearFiltersOffer}
              // B3: this band is 140px tall, not `60vh` — the full card
              // would cover it entirely (watched failing live). See
              // `CompactMapNoticePanel`'s own header.
              compact
            />
          )
        }
        mapSurface={
          <DirectoryMapSurface
            height="100%"
            directoryBasemap={directoryBasemap}
            chooseBasemap={chooseBasemap}
            fit={fit}
            signature={signature}
            fitReason={fitReason}
            holdFirstFit={holdFirstFit}
            onViewportChange={onViewportChange}
            searching={searching}
            cells={cells}
            q={q}
            onDrop={onMapDrop}
            nearMe={groundsNearYou.nearMe}
            pendingPin={pendingPin}
            mappable={mappable}
            iconFor={iconFor}
            facilitiesForGround={facilitiesForGround}
            facilityPins={facilityPins}
            onLocate={locate}
            // B3: this band is 140px tall, not `60vh` — cell bubbles that
            // are well clear of each other on the shipped page's wide map
            // project to overlapping pixels here. See `CellCountMarkers`.
            compact
          />
        }
      />
    </>
  );
}

export default function GroundsMap() {
  const { user } = useAuth();
  const isPlayer = user?.role === "player";
  // #1288 — a coach gets the redrawn list too (round-12 CoachGrounds); an admin keeps the shipped page.
  const isCoach = user?.role === "coach";
  const redrawn = newUi("grounds") && (isPlayer || isCoach);
  /**
   * Issue #683 — THE ARCHIVED OPT-IN IS ADMIN-ONLY.
   *
   * `include_archived=1` used to travel on every read this page made,
   * because the map was the only surface where a record keeper could see or
   * restore an archived venue. `/admin/venues` (#603) made that false, and
   * this flag is what tells `useDirectoryViewport`/`useDirectorySearch` to
   * stop asking for archived rows on behalf of anyone who isn't one — see
   * `lib/directoryLoad.js`'s `directoryBasePath`.
   */
  const isAdmin = user?.role === "admin";

  /**
   * #643 — THE DIRECTORY, FOR THE VIEWPORT ON SCREEN.
   *
   * `grounds` and `facilities` are what they always were: the venues this
   * map is drawing and the surfaces that draw on top of them (#166 — one
   * request for the whole layer, never one per ground; see the endpoint's own
   * header for why a client looping over ids is a different security shape,
   * not just a slower one). What changed is WHICH rows they hold — the ones
   * inside the snapped box the map is showing, re-read when it moves —
   * and that two more answers arrive beside them: `cells` (the count-per-area
   * contract, which the server returns when a box holds more grounds than it
   * will pin) and `truncationNotice`.
   *
   * `directoryError`/`facilitiesError` are unchanged in meaning and still
   * separate (#608): the two degrade differently, and a failed viewport read
   * writes NO rows at all, so the pins that were working stay on screen.
   */
  const directory = useDirectoryViewport(api, isAdmin);
  const { grounds, cells, mode, truncated, facilities, directoryError, facilitiesError, answered } = directory;
  const { truncationNotice, onViewportChange, retry: retryViewport, setGrounds, setFacilities } = directory;
  /**
   * Issue #683 — the box the CURRENT `grounds`/`mode` actually describe.
   * See `useDirectoryViewport`'s own header on `box`: it lands alongside the
   * rows it answers for, so the two can never disagree. `enrichDirectory`
   * uses it to keep a folded-in played/saved venue from widening the map's
   * fit to somewhere off screen (journey 15).
   */
  const { box: viewportBox } = directory;
  /**
   * Issue #166 — facilities on or off, default ON. A venue's nets are the
   * answer to "where can I practise", so hiding them by default would ship
   * the feature switched off; the toggle exists because a club with six
   * pinned surfaces puts six markers inside one ground's pin, and somebody
   * looking for the ground itself needs a way to quiet them.
   */
  const [showFacilities, setShowFacilities] = useState(true);
  const [playedVenues, setPlayedVenues] = useState([]);
  const [career, setCareer] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  /**
   * Issue #683 — the FULL rows behind `savedIds`, not just their ids.
   *
   * `savedIds` alone was always enough to answer "_saved" for a row already
   * in the directory. It cannot fold a saved venue BACK IN once the
   * directory read stops carrying archived rows for a player — that needs
   * the row itself (name, coordinates, `archived_at`), the same shape
   * `playedVenues` has always kept for exactly this reason. See the
   * `savedOnly` fold in `enriched` below.
   */
  const [savedVenues, setSavedVenues] = useState([]);

  const [q, setQ] = useState("");
  /**
   * The term, trimmed, in ONE place. Declared here rather than three hundred
   * lines down (where it used to sit) because #644 makes it the thing that
   * decides where the page's rows come from, so everything below reads it.
   */
  const term = q.trim();
  /**
   * Issue #644 — THE SEARCH, ANSWERED BY THE SERVER AND PAGED.
   *
   * The four `useMemo` chains this page used to run over the whole directory
   * were made WRONG rather than merely slow by #643: once the client holds
   * only the viewport's rows, a search that scans them finds only what is
   * already on screen — which is a worse answer than the one #617 was filed
   * to fix. So the term goes to `GET /api/grounds?q=`, the whole directory is
   * matched in SQL, and the answer comes back one page at a time.
   *
   * The rows the page reasons from are therefore the SEARCH's while a term is
   * active and the VIEWPORT's otherwise — one expression, `rows` below, so
   * nothing downstream has to know which.
   */
  const search = useDirectorySearch(api, q, isAdmin);
  const searching = Boolean(term);
  const rows = searching ? search.rows : grounds;
  // Issue #765 — "grounds near you" (wave 5, slice 1), behind
  // `newUi("grounds") && isPlayer` (PR #858 F6 — coaches and admins keep
  // this page exactly as shipped). Called UNCONDITIONALLY, per the Rules
  // of Hooks, alongside every other hook here. `term` is the SAME shared
  // `q`/`term` this component's own directory search already reads (F5):
  // the one search box narrows both the played list AND the shipped
  // directory's own search/notices/summary below, rather than each
  // reading a different value.
  const groundsNearYou = useGroundsNearYou({ api, isPlayer, isCoach, term });
  const [sourceFilter, setSourceFilter] = useState("all"); // all | played | mine | global
  // Issue #136. A SECOND, independent axis — "where is the game" and "where
  // can I practise" are different questions asked of the same directory, and
  // neither is a special case of the other. Composes with the search box,
  // the source chip and near-me rather than replacing any of them.
  const [typeFilter, setTypeFilter] = useState("all"); // all | field | nets | indoor

  const [selected, setSelected] = useState(null);
  // Issue #766, D167 G1/G3 — which ground the flag-on player's OWN card
  // (`components/GroundCard.jsx`) is open for, or null. A sibling of
  // `selected` rather than a reuse of it: `selected` backs the SHIPPED
  // dialog and the unbounded directory reads that feed it, which the
  // flag-on leg deliberately does not run (#643/#644).
  //
  // PR #921 review (F1) — holds the WHOLE row the player tapped, not just
  // an id: the played/nearby row already carries the ground's name and
  // coordinates, and `GroundCard.jsx` uses those as a `hint` to resolve
  // its own full record by a tight bbox or a name search — never the
  // unbounded, 1,000-row-capped directory read a bare id would otherwise
  // need.
  const [selectedCardGround, setSelectedCardGround] = useState(null);
  // Issue #131: the correction form is open for THIS ground, or null. Held
  // as an id rather than a boolean so that closing the dialog and opening a
  // different ground can never land you in a form seeded from the last one.
  const [editingGroundId, setEditingGroundId] = useState(null);
  /**
   * Issue #379: where the pin being corrected currently sits, or `null` when
   * no correction is open. Held on the page rather than in the form because
   * the MAP is the other half of the same control — the marker renders from
   * this, the form writes to it, and `via` records which of the two moved it
   * last so neither overwrites the other (`lib/groundEdit.js`).
   */
  const [correctionPin, setCorrectionPin] = useState(null);
  const [addGroundOpen, setAddGroundOpen] = useState(false);
  const [addGroundSeed, setAddGroundSeed] = useState("");
  const [pendingPin, setPendingPin] = useState(null);
  // #548: the add-surface pin — held on the page for #379's reason (the
  // detail MAP is the other half of the same control); shape and rules in
  // components/SurfacePinMap.jsx.
  const surfaceDraft = useSurfacePinDraft();

  // #583: the detail dialog only mounts a map when the selected ground has
  // coordinates (see the `detailHasMap && <MapContainer/>` guard below). This
  // is the single source of truth for "is there a host map on screen" — it
  // gates whether FacilityAddForm gets a real `pinDraft` (cause 1) and is what
  // the add-form scroll targets (cause 2).
  const detailHasMap = Boolean(selected && selected.latitude && selected.longitude);
  // #583 cause 2: the add-surface form silently arms the detail map (the hint
  // renders ON the map, which can be 280px above the fold). When the form opens
  // we scroll this wrapper into view so "the map became an input" is visible
  // from wherever the button was pressed.
  const detailMapRef = useRef(null);

  // ---------- Issue #134: "Use my location" ----------
  //
  // Held here and nowhere else — no storage, no logging, no request. The
  // position ranks the directory this page has ALREADY downloaded, on the
  // device (`lib/nearMe.js` over `lib/distance.js`), and dies when the page
  // does. The only way it becomes durable is the "Add this place" press,
  // which routes it through the SAME `pendingPin` the map's click-to-drop
  // has always used — a ground the user is choosing to create at that spot,
  // which is a different consent event from ranking a list.
  const [nearMe, setNearMe] = useState(NEAR_ME_IDLE);

  // ---------- Issue #183: street or satellite ----------
  //
  // ONE choice for both maps on this page — the directory map and the
  // ground-detail dialog — held here so the dialog, which remounts per
  // ground, opens on what the user last picked instead of resetting. `null`
  // means "never chosen", which is not the same as "street": the two
  // surfaces fall back differently (see `lib/basemap.js`) so the dialog
  // keeps opening on imagery until someone says otherwise.
  const [basemap, setBasemap] = useState(readBasemap);
  const chooseBasemap = useCallback((id) => {
    setBasemap(id);
    // The write can fail (quota, private mode); the choice still holds for
    // this session because the state above is what the maps render from.
    writeBasemap(id);
  }, []);
  const directoryBasemap = resolveBasemap(basemap, DIRECTORY_DEFAULT_BASEMAP);
  const detailBasemap = resolveBasemap(basemap, DETAIL_DEFAULT_BASEMAP);

  // ---------- Loaders ----------
  // The directory's own two reads moved to `hooks/useDirectoryViewport.js`
  // when #643 made them viewport-driven — the page can no longer fetch them
  // once on mount, because "once" has no viewport. Three rules that used to
  // live here and still hold, now asserted where they live:
  //
  //   - `include_archived=1` (#166 FAC-11): this page is the directory's
  //     management surface, the one place an archived venue must stay
  //     visible and badged. The box does not change that; every picker keeps
  //     the default read and never sees those rows.
  //   - #608/#633: a failure LEAVES THE LIST ALONE. `loadDirectoryGrounds`
  //     returns no `grounds` key at all on rejection, and the hook writes no
  //     rows on `ok: false`, so nothing can overwrite a good list with a
  //     fabricated empty one — which is how a 500 came to be reported as "No
  //     grounds here yet".
  //   - Success clears the flag, which is what makes Retry resolve the state
  //     rather than merely re-request.

  const loadSaved = useCallback(() => {
    if (!isPlayer) return Promise.resolve();
    return api
      .get("/players/me/grounds")
      .then((r) => {
        const rows = r.data || [];
        setSavedIds(new Set(rows.map((x) => x.ground_id)));
        // Issue #683 — the whole rows, not just the ids, so a saved venue
        // that has dropped out of the directory read (archived, and this
        // caller is not admin) can still be folded back onto the map. See
        // the `savedOnly` fold in `enriched` below.
        setSavedVenues(rows);
      })
      .catch(() => {});
  }, [isPlayer]);

  const loadPlayed = useCallback(() => {
    if (!isPlayer) return Promise.resolve();
    return api.get("/venues/played").then((r) => setPlayedVenues(r.data || [])).catch(() => setPlayedVenues([]));
  }, [isPlayer]);

  const loadCareer = useCallback(() => {
    if (!isPlayer) return Promise.resolve();
    return api.get("/analytics/career").then((r) => setCareer(r.data)).catch(() => setCareer(null));
  }, [isPlayer]);

  // #608's retry is `retryDirectory` off the hook above: both reads still go
  // together — the surfaces layer hangs off the same page load and is usually
  // down for the same reason — and it now re-asks for the viewport on screen
  // rather than for the whole directory.
  //
  // #643: there is no mount effect for the two directory reads any more. The
  // map itself is the trigger (`ViewportWatcher`), because a fetch on mount
  // is a fetch with no viewport, which is the unbounded read this epic
  // exists to remove.
  // #1494: the map's first arrival fit waits for BOTH personal lists (a ground the directory answer does not yet
  // carry reaches the map through them), so it frames every ground the player has. Both loaders catch their own
  // failures, so a failed list settles it too rather than holding the map on the world frame.
  const [personalDone, setPersonalDone] = useState(0);
  useEffect(() => { loadSaved().finally(() => setPersonalDone((n) => n | 1)); }, [loadSaved]);
  useEffect(() => { loadPlayed().finally(() => setPersonalDone((n) => n | 2)); }, [loadPlayed]);
  useEffect(() => { loadCareer(); }, [loadCareer]);
  const { view, foldVenues, nearYou, chips: viewChips, applyView, narrowedByPlace, holdFirstFit } = useGroundsView({ isCoach, groundsNearYou, savedVenues, redrawn, term, personalDone, nearMeReady: nearMe.status === "ready" });

  // ---------- Per-ground record, from statsCompute's by_ground (not recomputed) ----------
  const statsByName = useMemo(() => buildStatsByName(career), [career]);

  /**
   * Issue #683 — ARCHIVED VENUES, OFF THE PLAYER'S MAP, WITH ONE EXCEPTION.
   *
   * The merge itself — directory rows + everywhere played + everywhere
   * saved, each fold deduplicated against the others — moved to
   * `lib/directoryEnrich.js` for the reason `useDirectoryViewport` and
   * `useDirectorySearch` did (#643/#644): this component is measured
   * against `max-lines-per-function`, and it is an ordinary pure function
   * with nothing React-specific in it. THE PRINCIPLE that makes dropping an
   * unrelated archived venue while keeping a saved/played one consistent,
   * rather than a contradiction, is written there:
   *
   *   A badge is justified where the player had a prior expectation to
   *   explain, and unjustified where they did not.
   *
   * Decided by Martin/Andreas on #683: option (b).
   */
  // ---------- Unified directory: global/verified + mine + everywhere played (+ saved) ----------
  // Issue #683/#688 — `searching`/`pending` are two of the exact names (and
  // values) `directoryOutcome` already uses just below for the same
  // question ("is what's on screen a trustworthy answer right now?"); passed
  // straight through rather than re-derived. `viewportBox` is `null` while
  // searching or before the map's own read has ever landed, and
  // `enrichDirectory` treats that as "nothing to check against" — see its
  // own header. `mode` is NOT passed any more (#688): a saved/played venue
  // now folds in at any zoom, cells included — see `enrichDirectory`'s
  // `foldSuppressed` for why the cells-mode gate that used to sit here was
  // removed rather than kept as a no-op argument.
  const enriched = useMemo(
    () =>
      enrichDirectory({
        rows,
        playedVenues,
        savedVenues: foldVenues,
        statsByName,
        savedIds,
        term,
        searching,
        pending: search.pending,
        viewportBox,
      }),
    [rows, playedVenues, foldVenues, statsByName, savedIds, term, searching, search.pending, viewportBox],
  );

  /**
   * Issue #166 — the directory as a set of ids, and the facilities indexed
   * under it.
   *
   * `rows` DELIBERATELY, not `enriched`. `enriched` also carries
   * "played-only" venues — rows resolved by name from a player's match
   * history, which can name a ground that is NOT in this caller's directory
   * (another user's private pin, enriched read-time-only). Those are
   * legitimately drawn as read-only "played" pins, but they are exactly the
   * grounds whose facilities must never appear: the API sends none for them,
   * and indexing off the directory rows means the client would not draw one
   * if it did.
   *
   * #644 widened `grounds` to `rows` — the DIRECTORY rows in hand, which are
   * the search's answer while a term is active. The property is unchanged
   * (both are rows `GET /api/grounds` returned) and it is what lets a search
   * result outside the current viewport still show its surfaces once the map
   * has flown to it and the surfaces layer has caught up.
   */
  const visibleGroundIds = useMemo(() => new Set(rows.map((g) => g.ground_id)), [rows]);
  const facilitiesForGround = useMemo(
    () => facilitiesByGround(facilities, visibleGroundIds),
    [facilities, visibleGroundIds],
  );
  const facilitiesTotal = useMemo(() => facilityCount(facilitiesForGround), [facilitiesForGround]);

  /**
   * #644 — `searched` USED TO BE HERE, and its deletion is the point of this
   * slice. It filtered `enriched` by the term in the browser:
   *
   *     enriched.filter((g) => [g.name, g.city, g.country]
   *       .some((f) => f.toLowerCase().includes(term)))
   *
   * That was correct while the page held the whole directory. #643 made it
   * hold one viewport, and the same three lines then answered "where is Eden
   * Park" with "not on this screen" — a wrong answer, not a slow one. The
   * match now runs in SQL over every visible ground (`groundsSearchWhere`,
   * same three fields, same substring rule) and `rows` above is its answer.
   */

  /**
   * Issue #134's results, indexed — non-null only when the press actually
   * found something in range. Empty results deliberately do NOT restrict the
   * directory: "nothing near you" is answered with the offer to add the
   * place (below), not by blanking a page full of grounds.
   */
  /**
   * #134's answer, RE-RANKED whenever the directory changes — which #643
   * made necessary.
   *
   * `useMyLocation` ranks the directory the page has already downloaded, and
   * before this issue that was the whole directory, fetched once on mount.
   * It is now the viewport's rows: press "Use my location" at the map's
   * opening zoom and there is nothing loaded to rank, so the answer would be
   * "no grounds near you" — computed once, frozen, and still on screen a
   * moment later while the map flies to the player and their local pins
   * arrive around it. A number that is quietly wrong, which is the class
   * #617, #612 and #608 were each filed for.
   *
   * Deriving it fixes that by construction: the position is what the user
   * consented to (it is still read once, still held here, still sent
   * nowhere — `lib/nearMe.js`), and the RANKING is recomputed from whatever
   * the map currently holds. The press's own results seed it so the "ready"
   * state and its messages are unchanged for a user who already had the
   * directory.
   */
  /**
   * PR #858 review round 3 (B2, second half) — the redrawn screen presses
   * its OWN "Use my location", which drives `groundsNearYou.nearMe`, a
   * state this component otherwise never touches. `nearMeResults`/
   * `nearById`/`filtered` below used to key off the SHIPPED page's own
   * `nearMe` unconditionally, so on the redrawn leg pressing near-me left
   * `filtered` un-narrowed — still every world-viewport ground, including
   * ones a world away. `fitInput` (below) composes the device position
   * with that SAME un-narrowed `filtered`, so the resulting bounding box
   * still spanned the whole world and its SIGNATURE never changed —
   * `MapFitter` correctly saw "nothing new to fit" and never moved the
   * map. Watched failing live: `data-map-center` stuck at the identical
   * bounds signature before and after the press.
   *
   * `activeNearMe` is the one near-me state that actually drives this
   * page on whichever leg is rendering — identical to `nearMe` whenever
   * the shipped page itself is what's on screen, so nothing below changes
   * for it.
   */
  const activeNearMe = redrawn ? groundsNearYou.nearMe : nearMe;
  const nearMeResults = useMemo(() => {
    if (activeNearMe.status !== "ready" || !activeNearMe.position) return activeNearMe.results;
    return nearestGrounds(activeNearMe.position, enriched, { limit: MAP_NEAR_ME_LIMIT, maxKm: NEAR_ME_RADIUS_KM });
  }, [activeNearMe, enriched]);

  const nearById = useMemo(() => {
    if (activeNearMe.status !== "ready" || nearMeResults.length === 0) return null;
    return new Map(nearMeResults.map((g) => [g.ground_id, g]));
  }, [activeNearMe.status, nearMeResults]);

  const filtered = useMemo(() => {
    const chipped = enriched.filter((g) => {
      if (sourceFilter === "played" && !g._played) return false;
      if (sourceFilter === "mine" && !(g._saved || g._authored)) return false;
      if (sourceFilter === "global" && !g.is_global) return false;
      // Issue #136. A venue played at that matched no directory row has no
      // type (`place_type: null`), so any type filter hides it — correctly:
      // it cannot be shown as an answer to "show me the nets" when nothing
      // says it is one.
      if (typeFilter !== "all" && g.place_type !== typeFilter) return false;
      return true;
    });
    // #1411 (D367.19): the new look shows its own grounds plus the nearby ones, by default, and the chips change that.
    const viewed = applyView(chipped, nearById);
    if (viewed) return viewed;
    // Near-me composes with the search box and the chips rather than
    // replacing them: it is one more constraint on the same list, applied
    // last, and it brings the ordering with it.
    if (!nearById) return chipped;
    return chipped
      .filter((g) => nearById.has(g.ground_id))
      .map((g) => ({ ...g, _km: nearById.get(g.ground_id)._km, _distanceLabel: nearById.get(g.ground_id)._distanceLabel }))
      .sort((a, b) => a._km - b._km);
    // #644: `enriched`, not `searched` — the term is no longer one of the
    // narrowings this expression applies, because the rows it is narrowing
    // are already the term's answer.
  }, [enriched, sourceFilter, typeFilter, nearById, applyView]);

  /**
   * THE SOURCE-CHIP COUNTS, AND WHAT THEY HONESTLY COUNT (#644).
   *
   * Over `enriched` — the rows THE PAGE IS HOLDING, which is one page of a
   * search or one viewport of the directory. Not over a whole-directory
   * array, because there is not one any more, and not over `filtered`, which
   * these numbers exist to describe the alternatives to.
   *
   * `played`, `mine` and `global` cannot come off the server: "where I've
   * played" is answered by `/venues/played`, "mine" is a bookmark row in
   * another table, and neither is a predicate `GET /api/grounds` knows. #644
   * allows exactly this — server-derived OR honestly relabelled — and
   * relabelling is the option that does not invent three joins for a
   * dropdown. `chipCountLabel` is where the scope gets said out loud: the
   * label is unchanged (`All grounds (12)`) whenever these rows ARE every
   * matching row, and names the scope (`12 on this page`, `12 here`) when
   * they are not. That word is the whole difference between this and the
   * "All grounds (1)" #617 replaced.
   */
  const counts = useMemo(() => ({
    all: enriched.length,
    played: enriched.filter((g) => g._played).length,
    mine: enriched.filter((g) => g._saved || g._authored).length,
    global: enriched.filter((g) => g.is_global).length,
  }), [enriched]);

  /**
   * Issue #136 — counted over the SOURCE-chipped set, not over `searched`.
   * The two filters compose, so "Nets (0)" beside an active "Where I've
   * played" chip is the true answer to the question the user is actually
   * asking; counting over `searched` would advertise nets that pressing the
   * option cannot show them.
   */
  const typeCounts = useMemo(() => {
    const inSource = enriched.filter((g) => {
      if (sourceFilter === "played" && !g._played) return false;
      if (sourceFilter === "mine" && !(g._saved || g._authored)) return false;
      if (sourceFilter === "global" && !g.is_global) return false;
      return true;
    });
    return {
      all: inSource.length,
      field: inSource.filter((g) => g.place_type === "field").length,
      nets: inSource.filter((g) => g.place_type === "nets").length,
      indoor: inSource.filter((g) => g.place_type === "indoor").length,
    };
    // #644: over `enriched` for `counts`' reason — the rows in hand, whose
    // scope `chipCountLabel` names.
  }, [enriched, sourceFilter]);

  /**
   * Issue #644 — THE CARD LIST, PAGED, AND THE NUMBERS IT MAY STATE.
   *
   * Two sources, paged in two places, one shape out (`lib/directorySearch.js`
   * carries the argument):
   *
   *   A SEARCH is paged BY THE SERVER — `page`, `page_count` and the TRUE
   *   `matching` come off the answer, so "350 matches" is the directory's
   *   number rather than this page's.
   *
   *   THE VIEWPORT'S ROWS are paged HERE. They arrive as one bounded read
   *   (at most `GROUNDS_BBOX_MAX_ROWS`) because the map pins all of them at
   *   once; a thousand CARDS is 300,000 px of DOM nobody reaches the end of.
   *
   * `narrowed` is what stops the server's total being quoted over a list the
   * chips have cut down — #617's own defect ("a count above the map the grid
   * below does not show") is precisely what a paged answer makes easy to
   * commit again.
   */
  const [listPage, setListPage] = useState(1);
  const narrowed = sourceFilter !== "all" || typeFilter !== "all" || narrowedByPlace;
  const listing = useMemo(
    () => directoryListing({
      rows: filtered,
      answer: searching ? search.answer : null,
      page: listPage,
      narrowed,
      truncated,
      mode,
    }),
    [filtered, searching, search.answer, listPage, narrowed, truncated, mode],
  );
  const pageLine = describeDirectoryPage(listing);
  /**
   * #603's rule, kept: ANY change to what is being looked for returns to page
   * 1. Without it, narrowing while on page 3 leaves a reader staring at an
   * empty page indistinguishable from "nothing matches" — the paging bug
   * every list makes once. Keyed on one string rather than repeated in five
   * handlers, which is the version of this rule that survives a sixth being
   * added.
   */
  const resetKey = listingResetKey({ term, sourceFilter, typeFilter, nearMe: nearMe.status });
  const goToSearchPage = search.goToPage;
  useEffect(() => {
    setListPage(1);
    goToSearchPage(1);
  }, [resetKey, goToSearchPage]);
  const goToPage = useCallback(
    (next) => {
      const wanted = clampPage(next, listing.pageCount);
      if (listing.pagedByServer) goToSearchPage(wanted);
      else setListPage(wanted);
    },
    [listing.pageCount, listing.pagedByServer, goToSearchPage],
  );

  // ---------- Issue #212: where the map should look, and what it should say ----------
  //
  // Issue #134 composes with this rather than working around it: "fly to my
  // surroundings" changes WHICH fit is computed, and the result goes through
  // `applyFit` unchanged, including its reduced-motion rule. When the device
  // position is ready the fit is centred on it (`computeCentredFit`, #1609: the
  // box is built around the player, with the grounds near them inside it);
  // otherwise it fits the grounds alone (`computeMapFit`). Both are date-line
  // safe (#1619), so a fit across 180 stays narrow.
  const fit = useMemo(() => {
    if (activeNearMe.status !== "ready" || !activeNearMe.position) return computeMapFit(filtered, { wrap: redrawn });
    return computeCentredFit(activeNearMe.position, filtered, { wrap: redrawn });
  }, [filtered, activeNearMe, redrawn]);
  /**
   * #643 — WHAT MAKES A FIT A NEW QUESTION rather than a new answer. Read
   * `MapFitter`'s own header for why the two had to be separated: with the
   * directory fetched by viewport, the answer changes every time the map
   * moves, and fitting to that is a loop.
   *
   * Every axis a user can operate, and nothing that a pan can move on its
   * own. `nearMe.position` is in it because pressing "Use my location" is a
   * new question even when the status was already "ready".
   *
   * `answered` is in it because ARRIVING is a question too — the first one.
   * #212's opening behaviour is that the map fits itself to the directory
   * rather than sitting on the hard-coded mount frame, and that is what makes
   * a New Zealand player's grounds visible on a map centred over north
   * Africa. It is a one-way latch (`useDirectoryViewport`), so it changes
   * exactly once and every later answer leaves the view where the user put
   * it.
   */
  /**
   * #644 — THE TERM THE ROWS ON SCREEN ACTUALLY ANSWER, not the one in the
   * box. This is a one-word change with a whole journey behind it.
   *
   * `fitReason` used to read `q.trim()`, and that was right while the search
   * ran in the browser: `filtered` narrowed in the SAME render as the
   * keystroke, so "the question changed" and "the answer changed" were one
   * event. With the search on the server they are two, and the render in
   * between carries a NEW question beside the PREVIOUS answer.
   *
   * `MapFitter` consumes a reason the first time it sees one it can act on
   * (see its own header). Handed the new term next to the old rows, it
   * consumed the reason, found the signature unchanged, and returned without
   * moving — and when the real rows landed a moment later the reason was
   * already spent, so the map never went. Journey 1 caught it exactly:
   * searching a ground in London from a map looking at Auckland left the view
   * in Auckland while the card list showed London.
   *
   * Keying on the ANSWERED term makes the reason change at the moment the
   * answer does, which restores the pre-#644 identity without pretending the
   * request is synchronous. A search that FAILED counts as answered — the
   * page says so and the map correctly stays where it was.
   */
  const answeredTerm = searching ? search.answeredTerm : "";
  const fitReason = useMemo(
    () => [answered, answeredTerm, sourceFilter, typeFilter, activeNearMe.status, activeNearMe.position ? `${activeNearMe.position.lat},${activeNearMe.position.lng}` : ""].join("|"),
    [answered, answeredTerm, sourceFilter, typeFilter, activeNearMe.status, activeNearMe.position],
  );
  const signature = useMemo(() => fitSignature(fit), [fit]);

  // The MATCHING GROUNDS that can be pinned — the set the map actually
  // draws. Named (issue #166) because a second thing now hangs off it: a
  // facility is drawn only for a ground that is itself on the map, so the
  // two must come from one expression rather than two that can drift.
  const mappable = useMemo(() => filtered.filter(hasCoords), [filtered]);
  // Not `fit.pointCount` — that also counts the "you are here" point when
  // near-me is on, and the notices below are counting grounds.
  const mappableCount = mappable.length;

  /**
   * Issue #166 — the facility pins, from the grounds already being drawn.
   *
   * Empty when the toggle is off, which is the whole implementation of the
   * toggle: nothing is hidden with CSS, the markers are simply not created,
   * so leaflet is not carrying them and a screen reader is not reading them.
   */
  const facilityPins = useMemo(
    () => (showFacilities ? facilityMarkers(mappable, facilitiesForGround) : []),
    [showFacilities, mappable, facilitiesForGround],
  );

  // Grounds that matched but carry no coordinates. Legacy free-text venues
  // predate the geocoded directory and there is nothing to fit them to —
  // they stay in the results list (they are real places the player has
  // played), they simply cannot be pinned. Saying so is the difference
  // between "the map is broken" and "the map has nothing to draw".
  const unmappableCount = filtered.length - mappableCount;

  /**
   * #608's rule, over BOTH reads that can fill this list (#644). A failed
   * search and a failed viewport read are the same fact to a reader — the
   * list is empty for a reason that is not emptiness — and both have the same
   * remedy, which is to try again rather than to add a ground.
   */
  const listError = searching ? search.searchError : directoryError;
  const retryDirectory = searching ? search.retry : retryViewport;

  /**
   * What the MAP says when it has nothing to move to. The list below
   * already carried a "no grounds match" line, but on a map-led screen the
   * map is where the eye is — a message only in the grid underneath is a
   * message the user scrolls past while staring at an unchanged world view.
   */
  const mapNotice = useMemo(() => {
    // #608 FIRST, before any emptiness is described: every branch below reasons
    // from `filtered.length`, which is 0 for a failed fetch and for a genuinely
    // empty directory alike. The list cannot tell them apart, so the flag is
    // consulted before the list is. The remedy is a retry, not "add a ground".
    const outcome = directoryOutcome({
      failed: listError,
      count: filtered.length,
      mode,
      // #644: while a term is active the list is the SEARCH's answer, so the
      // viewport's mode is not about it — and a question with no answer yet
      // is not an empty one.
      searching,
      pending: search.pending,
    });
    if (outcome === "failed") {
      return { kind: "load-failed", title: DIRECTORY_ERROR_TITLE, body: DIRECTORY_ERROR_BODY, cta: null };
    }
    // Nothing to say yet: the term has been typed and its answer is in
    // flight. Saying "No grounds match" here would be a wrong answer rather
    // than a slow one — see `directoryOutcome`.
    if (outcome === "pending") return null;
    // #643: a THIRD reason for `filtered.length` to be 0, with a third
    // remedy. When a box holds more grounds than the server will pin it
    // sends counts per area and no rows at all (#642/#643), so every branch
    // below would describe an emptiness that is not there — to a user
    // looking at bubbles counting twenty thousand grounds. Saying "no
    // grounds here yet" to them would be #608's defect produced by a
    // different mechanism.
    if (outcome === "aggregated") {
      // #644 REMOVED THIS BRANCH'S SECOND SENTENCE. #643 had to say "Zoom in
      // to search here" when a term was active over an aggregated answer,
      // because the search filtered the rows the viewport had loaded and an
      // aggregate answer loads none. The search no longer reads the
      // viewport's rows at all, so `outcome` can only be "aggregated" with no
      // term (`directoryOutcome`), and this state is once again the plain
      // one: a map showing counts because there are too many to pin.
      return {
        kind: "aggregated",
        title: "Showing how many grounds are in each area",
        body: "There are too many to pin at this zoom. Tap a number, or zoom in, to see the grounds themselves.",
        cta: null,
      };
    }
    if (filtered.length === 0) {
      // Three different empty states wearing one shape. Telling them apart
      // matters: "nothing matched your search", "your filter hid it" and
      // "you haven't added anything yet" have three different remedies, and
      // a directory that is simply empty must not be reported as a filter
      // problem — that is the state a brand-new player lands in.
      // Issue #134 adds a fourth: near-me is one more constraint on the same
      // list, so when it is on it can be the thing that emptied it, and the
      // remedy ("clear near me") is different again.
      const near = nearMe.status === "ready";
      // Issue #136: the type filter is a fifth thing that can empty this
      // list, so it counts as "a filter is on" for the purpose of telling an
      // EMPTY DIRECTORY apart from a hidden one. Reporting "No grounds here
      // yet" to somebody who has simply selected Nets is the exact confusion
      // #212 separated these states to avoid.
      const filtersOn = sourceFilter !== "all" || typeFilter !== "all";
      const empty = !term && !filtersOn && !near;
      return {
        kind: "no-matches",
        title: term ? `No grounds match “${term}”` : empty ? "No grounds here yet" : near ? "No grounds near you under this filter" : "No grounds under this filter",
        // Naming the scope is the point of issue #212's third item: this
        // box searches the directory, the add flow searches the world, and
        // a user who has just used the worldwide search has every reason to
        // expect the same reach here.
        body: term
          ? `This searches grounds already in your directory${near ? `, and near-me has it narrowed to ${NEAR_ME_RADIUS_KM} km around you` : ""}. The map has stayed where it was — there's nothing here to show you.`
          : empty
            ? "Add a ground and it'll be pinned here — the worldwide search will find it and bring its coordinates with it."
            : near
              ? `Nothing within ${NEAR_ME_RADIUS_KM} km of you is under this filter. The map has stayed where it was.`
              : "The map has stayed where it was — there's nothing under this filter to show you.",
        cta: term ? `Search worldwide for “${term}”` : "Add a ground",
      };
    }
    if (mappableCount === 0) {
      const one = filtered.length === 1;
      return {
        kind: "no-coordinates",
        title: "Nothing to pin here",
        body: `${filtered.length} matching ground${one ? "" : "s"} ${one ? "has" : "have"} no coordinates yet, so the map can't place ${one ? "it" : "them"}. ${one ? "It's" : "They're"} in the results list.`,
        cta: null,
      };
    }
    return null;
    // #643 appends `mode`: which contract the last answer came back under is
    // now one of the things that decides what this notice says. #644 appends
    // `searching` and `search.pending`, and swaps `directoryError` for
    // `listError` — a failed SEARCH is a failure of this list just as a
    // failed viewport read is.
  }, [listError, filtered.length, mappableCount, term, sourceFilter, typeFilter, nearMe.status, mode, searching, search.pending]);

  // ---------- Actions ----------
  const openAddGround = (seed = "") => {
    setPendingPin(null);
    setAddGroundSeed(seed);
    setAddGroundOpen(true);
  };

  /**
   * Issue #134. Fires only from the button's click handler — never on mount,
   * never on a render. The directory is already in `enriched` (this page
   * downloads it on load), so `findNearMe` gets it as an argument and the
   * position never has a request to travel on.
   */
  const useMyLocation = async () => {
    setNearMe({ ...NEAR_ME_IDLE, status: "locating" });
    const outcome = await findNearMe(enriched, { limit: MAP_NEAR_ME_LIMIT, maxKm: NEAR_ME_RADIUS_KM });
    if (!outcome.ok) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: outcome.message });
      return;
    }
    setNearMe({ status: "ready", results: outcome.results, message: "", position: outcome.position });
  };

  const clearNearMe = () => setNearMe(NEAR_ME_IDLE);

  /**
   * The second explicit press: nothing within the radius, so add this spot.
   * Goes through the SAME `pendingPin` + `presetCoords` path as dropping a
   * pin by hand (`GroundSearchDialog`, which reverse-geocodes city/country)
   * — the one place the position is meant to become a record, because the
   * record is a public ground the user is choosing to create.
   */
  const addHere = () => {
    if (!nearMe.position) return;
    setAddGroundSeed("");
    // `fromDevice` marks this pin as a reading of where the PLAYER is, not
    // a spot they clicked — the dialog drops the coordinates from its
    // duplicate check on that basis (#134's adversarial review).
    setPendingPin({ lat: nearMe.position.lat, lng: nearMe.position.lng, fromDevice: true });
    setAddGroundOpen(true);
  };

  const toggleSaved = async (g, e) => {
    e?.stopPropagation?.();
    if (!isPlayer) return;
    try {
      if (savedIds.has(g.ground_id)) {
        await api.delete(`/players/me/grounds/${g.ground_id}`);
        setSavedIds((prev) => { const n = new Set(prev); n.delete(g.ground_id); return n; });
        toast.success(`Removed "${g.name}" from My Grounds`);
      } else {
        await api.post("/players/me/grounds", { ground_id: g.ground_id });
        setSavedIds((prev) => new Set(prev).add(g.ground_id));
        toast.success(`Added "${g.name}" to My Grounds`);
      }
    } catch { toast.error("Could not update My Grounds"); }
  };

  const foldCreatedGround = (g) => {
    // A user-created ground is auto-saved server-side (issue #57); refresh
    // local state instead of re-fetching the whole directory.
    setGrounds((prev) => (prev.some((x) => x.ground_id === g.ground_id) ? prev : [g, ...prev]));
    setSavedIds((prev) => new Set(prev).add(g.ground_id));
    setPendingPin(null);
  };

  /**
   * #1143 — the redrawn Grounds screen's own add-a-ground step. The row the
   * POST returned is folded into the map's pins, the near-me list and the
   * search (one fresh search read when a term is active — the one list that
   * cannot be patched, since the server pages and counts it). The step's own
   * toast already ran; the add itself is not sent twice.
   */
  const onGroundAdded = (g) => {
    foldCreatedGround(g);
    groundsNearYou.addGround(g);
    search.refresh();
  };

  /**
   * D258 — "Use this ground" in the redrawn add step saves the existing
   * ground to the player's grounds, the call `GroundSearchDialog`'s
   * `linkDuplicate` makes (#58), so it survives a reload. Players only: a
   * role with no player profile would 404. Other hosts of the panel (the log
   * and Plan flows) pass nothing and only pick the ground.
   */
  const onUseExistingGround = isPlayer
    ? async (g) => {
        await api.post("/players/me/grounds", { ground_id: g.ground_id });
        // The card reads "Saved" from this set; the add step does not fold a
        // used ground in as new, so this is the only place that marks it.
        setSavedIds((prev) => new Set(prev).add(g.ground_id));
      }
    : undefined;

  const onGroundCreated = (g) => {
    foldCreatedGround(g);
    toast.success(`"${g.name}" added — you can log matches here now.`);
  };

  // #547 — the duplicate prompt's "add a field or net to this ground
  // instead" route; the rules live on the hook, module level, where the
  // maintainability budget wants them.
  const surfaceInstead = useSurfaceInstead(enriched, user, setSelected, setSelectedCardGround, isPlayer);

  /**
   * Issue #131 — a correction landed. The directory list and the open dialog
   * are updated from the row the server returned, not from what was typed:
   * the server is the one that decides what a correction actually did
   * (it trims, and it stamps `updated_at`/`updated_by`), and showing the
   * typed value would quietly disagree with the next page load.
   *
   * `selected` is refreshed rather than closed, because the person who just
   * moved a pin wants to see where it landed.
   */
  /**
   * Issue #379: opening the correction form seeds the movable pin from where
   * the ground actually is, so the first drag starts from the mistake rather
   * than from nowhere. Closing it drops the pin — a half-moved position must
   * not survive a Cancel, or reopening the form would offer to save a change
   * the user already backed out of.
   */
  const startCorrecting = (g) => {
    setEditingGroundId(g.ground_id);
    setCorrectionPin(groundPin(g));
  };
  const stopCorrecting = () => {
    setEditingGroundId(null);
    setCorrectionPin(null);
  };

  const onGroundCorrected = (g) => {
    setGrounds((prev) => prev.map((x) => (x.ground_id === g.ground_id ? { ...x, ...g } : x)));
    setSelected((prev) => (prev && prev.ground_id === g.ground_id ? { ...prev, ...g } : prev));
    stopCorrecting();
    toast.success(`"${g.name}" updated`);
  };

  /**
   * Issue #166 FAC-09 / #542 — one surface re-rated, archived, or NEWLY
   * REGISTERED, folded back into the list the whole page reads from.
   *
   * THE SERVER'S ROW, not a locally-patched one. `condition_rated_at` can
   * only come from the server clock (#164), so a client that merged its own
   * guess would render a rating dated by the device that typed it — the exact
   * failure the column exists to prevent. The response is the row.
   *
   * No refetch: `GET /grounds/facilities` answers for the whole directory,
   * and re-reading every venue's surfaces to learn one net's new rating is a
   * request the response in hand already contains. #542 made the fold an
   * UPSERT (`upsertFacilityRow`) for the same reason: the 201 body IS the
   * new row, and appending it is what makes the surface appear in the open
   * dialog — condition control, rating block and all — with no reload.
   */
  const onFacilityRated = (f) => {
    setFacilities((prev) => upsertFacilityRow(prev, f));
    toast.success(`"${f.name}" saved`);
  };

  /**
   * Issue #136. Colour on the source dimension exactly as before; glyph on
   * the type. `?` for a type the client cannot read (`placeTypeGlyph`) —
   * unreachable, since `place_type` is NOT NULL, and visibly odd rather
   * than silently drawn as a field if it ever is.
   */
  const iconFor = (g) => {
    const source = g._played ? "played" : (g._saved || g._authored) ? "mine" : "global";
    return groundIcons[source][g.place_type] ?? makeIcon(SOURCE_COLORS[source], placeTypeGlyph(g.place_type));
  };

  /**
   * Issue #379: is the ground currently open ALSO the one being corrected?
   * Both halves matter — `editingGroundId` outliving the dialog it belongs to
   * is exactly the desync #131 held as an id rather than a boolean to avoid,
   * and a draggable marker on a ground nobody is editing is a mis-pin waiting
   * to happen.
   */
  const correcting = !!selected && editingGroundId === selected.ground_id;
  const surfaceArrival = surfaceInstead.arrivalFor(selected, correcting);

  /**
   * Issue #617 — the search's answer, and where it goes.
   *
   * Summarised off `filtered`, the same set the cards render and the map fits
   * to, so the line above the map cannot advertise a count the list below it
   * does not show. `mapOrder` is "" whenever no search is active, which is what
   * leaves the untouched directory rendering exactly as it always has.
   */
  const searchSummary = useMemo(
    // #644: `searchSummaryScope` hands it the SERVER's total when that total
    // still describes what is on screen, and says "on this page" when a chip
    // is narrowing a multi-page answer. A pending search says nothing at all
    // rather than "No matches", which would be a wrong answer for a quarter
    // of a second — see `useDirectorySearch`.
    () => (search.pending ? null : searchResultSummary(term, filtered, searchSummaryScope(listing))),
    [term, filtered, listing, search.pending],
  );
  const mapOrder = mapBlockOrderClass(searchSummary);
  // Offered only when a chip is actually narrowing and there is something to
  // go back to — shared by the search line and the map notice, so the two can
  // never disagree about whether the escape exists.
  const clearFiltersOffer =
    (sourceFilter !== "all" || typeFilter !== "all") && counts.all > 0
      ? { count: counts.all, onClear: () => { setSourceFilter("all"); setTypeFilter("all"); } }
      : null;
  const gridRef = useRef(null);
  const jumpToResults = useCallback(() => scrollResultsIntoView(gridRef.current), []);

  // Issue #765 — a rebuilt screen REPLACES the page (epic #749 decision,
  // 2026-09-23). PR #858 review round 1 (F6): players only; #1288 added the
  // coach. An admin always falls through to the shipped page below,
  // unconditionally on the flag, so their experience (including the admin's
  // own archived-venue view) is untouched.
  //
  // PR #921 review (F4) — the whole player branch is its OWN component,
  // `GroundsPlayerScreen` (module scope, below), rather than inline JSX
  // here: this function is already the file's most complex by a wide
  // margin, and #766's own card-vs-list branch used to add two more
  // decision points directly to it (pushing `complexity` from the
  // pre-#766 baseline to 66, one over the 65 budget) — real complexity, not
  // hidden, but a `GroundsMap`-shaped function is not where it should
  // live. Moving the whole branch out returns THIS function to exactly
  // its pre-#766 shape (one `if`, one return) and gives the branch its
  // own low-complexity home instead.
  if (redrawn) {
    return (
      <GroundsPlayerScreen
        audience={isCoach ? "coach" : "player"}
        selectedCardGround={selectedCardGround}
        arrivalFor={surfaceInstead.arrivalFor}
        onArrivalConsumed={surfaceInstead.clear}
        onCardBack={() => {
          setSelectedCardGround(null);
          // The card's own correction/facility writes land on ITS OWN
          // local state (`GroundCard.jsx`'s header explains why — this
          // page's `playedVenues` is a sibling cache, not shared with
          // it), so the played/nearby list this returns to would
          // otherwise still show whatever it had before the card opened.
          // `reload` is the same played-list re-fetch the "couldn't
          // load" retry already uses.
          groundsNearYou.reload();
        }}
        savedIds={savedIds}
        onToggleSaved={toggleSaved}
        groundsNearYou={nearYou}
        onOpenGround={setSelectedCardGround}
        term={term}
        searchText={q}
        setTerm={setQ}
        searchPending={search.pending}
        searchSummary={searchSummary}
        onSearchWorldwide={() => openAddGround(term)}
        clearFiltersOffer={clearFiltersOffer}
        mapNotice={mapNotice}
        onRetryDirectory={retryDirectory}
        directoryBasemap={directoryBasemap}
        chooseBasemap={chooseBasemap}
        fit={fit}
        signature={signature}
        // #1411: switching the view is a new question for the fitter.
        fitReason={`${fitReason}|${view}`}
        holdFirstFit={holdFirstFit}
        viewChips={viewChips}
        onViewportChange={onViewportChange}
        searching={searching}
        cells={cells}
        q={q}
        onMapDrop={(pos) => { setAddGroundSeed(""); setPendingPin(pos); setAddGroundOpen(true); }}
        pendingPin={pendingPin}
        mappable={mappable}
        iconFor={iconFor}
        facilitiesForGround={facilitiesForGround}
        facilityPins={facilityPins}
        addGroundOpen={addGroundOpen}
        onAddGroundOpenChange={(o) => { setAddGroundOpen(o); if (!o) { setPendingPin(null); setAddGroundSeed(""); } }}
        addGroundSeed={addGroundSeed}
        onGroundAdded={onGroundAdded}
        onUseExistingGround={onUseExistingGround}
        // A coach's card has no "Add a field or net" (D301 Q4), so the duplicate
        // prompt does not offer to route there.
        onAddSurfaceInstead={isCoach ? undefined : surfaceInstead.route}
      />
    );
  }

  return (
    // `flex flex-col gap-6`, not `space-y-6` (#617): the gap has to survive the
    // map being re-ordered below the results, and `space-y-*` spaces DOM
    // siblings, which is a different sequence once `order` is in play.
    <div className="flex flex-col gap-6" data-testid="grounds-map-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Discover</p>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Grounds Directory</h1>
        <p className="text-slate-500 mt-1">One unified library — global venues, your saved grounds, and everywhere you've played.</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:flex-1 sm:min-w-[240px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            {/* #644: bounded to the same length the endpoint will answer
                (`GROUNDS_SEARCH_MAX_LENGTH`), so a paste cannot produce a
                request the server refuses. The server refuses one anyway —
                a bound enforced only in a text input is not a bound. */}
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              maxLength={GROUNDS_SEARCH_MAX_LENGTH}
              placeholder="Search grounds, cities, countries…"
              className="pl-9 h-10"
              data-testid="grounds-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-44 h-10" data-testid="grounds-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {/* #644: the count with its SCOPE, whenever the rows these
                    numbers describe are not every matching row. Unchanged
                    (`All grounds (12)`) when they are. */}
                <SelectItem value="all">{chipCountLabel("All grounds", counts.all, listing)}</SelectItem>
                {isPlayer && <SelectItem value="played">{chipCountLabel("Where I've played", counts.played, listing)}</SelectItem>}
                <SelectItem value="mine">{chipCountLabel("My grounds", counts.mine, listing)}</SelectItem>
                <SelectItem value="global">{chipCountLabel("Global directory", counts.global, listing)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Issue #136. A separate control beside the source one, not a
              fourth group of options inside it: they are independent axes
              and a single list would force a user who wants "my nets" to
              choose which half of that sentence to keep.
              A SIBLING of the source group rather than a child of it, so
              that at 390px the card's `flex-wrap` can drop it to its own
              line — nested, the two selects are one unwrappable item and the
              page scrolls sideways (caught by the responsive harness). */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 h-10 min-w-0" data-testid="grounds-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLACE_TYPE_FILTERS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {chipCountLabel(o.label, typeCounts[o.value], listing)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Issue #134. Explicit press, nothing on load. The busy state is a
              word rather than a spinner, so it reads the same with reduced
              motion on — the only motion this feature adds is the map fit,
              which goes through #212's `applyFit` and already honours it. */}
          <Button
            variant="outline"
            onClick={useMyLocation}
            disabled={nearMe.status === "locating"}
            className="min-h-11 border-[#0B2E59] text-[#0B2E59]"
            data-testid="grounds-near-me-btn"
          >
            <LocateFixed size={16} className="mr-2 shrink-0" />
            <span className="truncate">{nearMe.status === "locating" ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}</span>
          </Button>
          {/* Issue #166. Offered only when there is something to toggle: a
              control that never changes anything is noise on a filter bar
              that is already at its width budget (390px, #136's wrap note).
              The COUNT is on the label so pressing it is a decision rather
              than an experiment. */}
          {facilitiesTotal > 0 && (
            <Button
              variant={showFacilities ? "default" : "outline"}
              onClick={() => setShowFacilities((v) => !v)}
              className="min-h-11 min-w-0"
              style={showFacilities ? { background: FACILITY_COLOR } : { borderColor: FACILITY_COLOR, color: FACILITY_COLOR }}
              aria-pressed={showFacilities}
              data-testid="grounds-facilities-toggle"
            >
              <LayoutGrid size={16} className="mr-2 shrink-0" />
              <span className="truncate">Facilities ({facilitiesTotal})</span>
            </Button>
          )}
          <Button onClick={() => openAddGround()} data-testid="grounds-add-btn" style={{ background: "#0B2E59" }} className="ml-auto min-h-11">
            <MapPinPlus size={16} className="mr-2" /> Add ground
          </Button>
        </CardContent>
      </Card>

      {/* #617: the count and the names, under the box they were typed into.
          Above the near-me banner and everything else, because it is the
          direct answer to the most recent keystroke. */}
      <SearchResultLine
        summary={searchSummary}
        onJump={jumpToResults}
        onSearchWorldwide={() => openAddGround(term)}
        clearFilters={clearFiltersOffer}
      />

      {/* What near-me did, said once, above the map it just moved. */}
      <NearMeBanner nearMe={nearMe} results={nearMeResults} onAddHere={addHere} onClear={clearNearMe} />

      <DirectoryLegend isPlayer={isPlayer} facilitiesTotal={facilitiesTotal} className={mapOrder} />

      {/* Map. #617: `mapOrder` drops this — and the legends above it — BELOW the
          result grid on a narrow viewport while a search is active. The map is
          context; the list is the answer, and the answer belongs where the
          question was typed. Empty string, so DOM order, the rest of the time. */}
      <Card className={`border-slate-200 overflow-hidden ${mapOrder}`}>
        <CardContent className="p-0">
          <div style={{ height: "60vh" }} className="relative isolate">
            {/* #687: `noWrap` on the tile layer stops the copies from being
                drawn, but leaves the panel free to pan into the now-empty
                space beside the one world it does draw. `maxBounds` closes
                that gap — clamped to Web Mercator's real latitude limit
                (±85.05, not ±90, which projects to infinity) — and
                `maxBoundsViscosity={1}` makes the edge solid rather than
                elastic, so a drag cannot overshoot into a repeat and spring
                back. `mapViewport.js` still widens a world-width viewport to
                the full −180…180 exactly once; this clamp does not change
                that fact, it only stops the user from panning past it. */}
            <DirectoryMapSurface
              height="100%"
              directoryBasemap={directoryBasemap}
              chooseBasemap={chooseBasemap}
              fit={fit}
              signature={signature}
              fitReason={fitReason}
              onViewportChange={onViewportChange}
              searching={searching}
              cells={cells}
              q={q}
              onDrop={(pos) => { setAddGroundSeed(""); setPendingPin(pos); setAddGroundOpen(true); }}
              nearMe={nearMe}
              pendingPin={pendingPin}
              mappable={mappable}
              iconFor={iconFor}
              facilitiesForGround={facilitiesForGround}
              facilityPins={facilityPins}
            />

            {/* Issue #212: the map says what happened, in the place the eye is. */}
            <DirectoryMapNoticeOverlay
              mapNotice={mapNotice}
              onAdd={() => openAddGround(term)}
              onRetry={retryDirectory}
              clearFiltersOffer={clearFiltersOffer}
            />
            {/* #643/#609: degradation is VISIBLE. The server caps a viewport
                read and says so; this is where the page says so too, at the
                bottom so it can coexist with the notice above rather than
                displacing it. */}
            <MapTruncationNotice text={truncationNotice} />
          </div>
          {/* Grounds that matched but can't be fitted: they stay in the list,
              and the map admits they're missing rather than under-reporting. */}
          <FacilitiesLoadError failed={facilitiesError} directoryFailed={directoryError} />
          {unmappableCount > 0 && mappableCount > 0 && (
            <p className="px-4 py-2 text-xs text-slate-500 border-t border-slate-200" data-testid="grounds-map-unmapped-note">
              {unmappableCount} more match{unmappableCount === 1 ? "" : "es"} {unmappableCount === 1 ? "has" : "have"} no coordinates yet and can't be pinned — {unmappableCount === 1 ? "it's" : "they're"} in the results list.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Ground cards */}
      {/* #617: `gridRef` is what "Show in list" scrolls to, and on a narrow
          viewport under an active search this block renders ABOVE the map
          rather than a screen and a half below it. */}
      {/* #644: the range and the total, above the cards they describe, with
          the scope named whenever the total is not "everything that matches"
          — #603's requirement and #612's lesson in one line. */}
      {pageLine && (
        <p className="text-xs text-slate-500" data-testid="grounds-directory-count" role="status" aria-live="polite">
          {pageLine}
        </p>
      )}
      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grounds-directory-grid">
        {listing.rows.map((g) => (
          <GroundCard
            key={g.ground_id}
            g={g}
            isPlayer={isPlayer}
            /* Issue #166: the card reads what the venue is made of without
               being opened, on `PlaceTypeBadge`'s argument — a fact a player
               scanning a list needs is a fact that must not require a click. */
            facilities={facilitiesForGround.get(g.ground_id)}
            onOpen={() => setSelected(g)}
            onToggleSave={(e) => toggleSaved(g, e)}
          />
        ))}
        {/* #608: the grid's own zero line describes a FILTER, so it is just as
            wrong on a failed fetch as the map's was. The failure owns this slot
            when there is one, and offers the same retry. #643 adds the third
            reason this list can be empty — see `DirectoryGridEmpty`, which is
            where all three now live so that the rule is one function rather
            than a nested ternary in JSX. */}
        <DirectoryGridEmpty
          directoryError={listError}
          mode={mode}
          count={filtered.length}
          searching={searching}
          pending={search.pending}
          onRetry={retryDirectory}
        />
      </div>
      {/* #644: the way to the rest of the answer. Below the grid, because a
          pager above a list you have not read yet is furniture. */}
      <DirectoryPager listing={listing} onGoTo={goToPage} />

      {/* Ground detail dialog */}
      <Dialog
        open={!!selected}
        onOpenChange={(o) => {
          if (!o) {
            setSelected(null);
            stopCorrecting();
            surfaceDraft.close();
            surfaceInstead.clear();
          }
        }}
      >
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {selected && <DialogTitle className="sr-only">{selected.name}</DialogTitle>}
          {selected && (
            <>
              {detailHasMap && (
                <div ref={detailMapRef} style={{ height: "280px" }} className="relative bg-slate-100">
                  <MapContainer
                    key={`ground-map-${selected.ground_id}`}
                    center={[selected.latitude, selected.longitude]}
                    zoom={17}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={false}
                  >
                    {/* Issue #183 point 3: this map was satellite-only. It
                        keeps opening on imagery (that is what makes a ground
                        recognisable, and what was asked for) but street is
                        now one click away, and the choice is the same one
                        the directory map above is using. */}
                    <Basemaps active={detailBasemap} onChoose={chooseBasemap} />
                    {/* Issue #379: while the correction form is open, a click
                        anywhere on this map re-pins the ground. Off at every
                        other time — a stray tap on a ground you are only
                        looking at must not move it. */}
                    {correcting && <PinDropCatcher onDrop={(pos) => setCorrectionPin({ ...pos, via: GROUND_PIN_FROM_MAP })} />}
                    {/* #548: while the ADD-SURFACE form is open instead, the
                        same taps place the surface's pin. Never both. */}
                    {!correcting && surfaceDraft.active && <SurfacePinLayer pin={surfaceDraft.pin} onMove={surfaceDraft.setPin} />}
                    <Marker
                      // #1176: focusable, so named; re-keyed because leaflet
                      // reads `title` once.
                      key={correcting ? "pin-move" : "pin-view"}
                      // View mode: the pin does nothing, so it is not a Tab stop.
                      keyboard={correcting}
                      title={correcting ? `Move pin: ${selected.name}` : selected.name}
                      // Issue #379: the marker follows the correction while
                      // one is open — from a drag, from a tap on the map, or
                      // from the coordinate fields below — and falls back to
                      // the saved position the rest of the time.
                      position={correcting && isGroundPin(correctionPin)
                        ? [correctionPin.lat, correctionPin.lng]
                        : [selected.latitude, selected.longitude]}
                      icon={correcting ? movablePinIcon : iconFor(selected)}
                      draggable={correcting}
                      // `dragend`, not `drag`: leaflet fires `drag` on every
                      // animation frame, which would re-render the form's
                      // fields (and schedule a geocode) for every pixel.
                      eventHandlers={correcting ? {
                        dragend: (e) => {
                          const moved = e.target.getLatLng();
                          setCorrectionPin({ lat: moved.lat, lng: moved.lng, via: GROUND_PIN_FROM_MAP });
                        },
                      } : undefined}
                    />
                    {!correcting && !surfaceDraft.active && <DetailFacilityMarkers list={facilitiesForGround.get(selected.ground_id)} />}
                  </MapContainer>
                  {/* Named on the map itself as well as in the form: the
                      person who came to fix a pin is looking HERE, and #379
                      exists because the one route that was discoverable was
                      the number fields. `pointer-events-none` so the hint
                      never eats the tap it is describing. */}
                  {correcting && (
                    <div
                      className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center p-2"
                      data-testid="ground-edit-pin-hint"
                      role="status"
                    >
                      <p className="rounded-full bg-slate-900/85 px-3 py-1 text-[11px] font-semibold text-white shadow">
                        Drag the pin, or tap the map, to move it
                      </p>
                    </div>
                  )}
                  {!correcting && surfaceDraft.active && <SurfacePinHint />}
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h2 className="font-display font-black text-2xl text-slate-900">{selected.name}</h2>
                    <div className="flex items-center gap-1 text-slate-500 mt-1"><MapPin size={14} /> {[selected.city, selected.country].filter(Boolean).join(", ")}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <PlaceTypeBadge g={selected} large />
                    <SourceBadge g={selected} large />
                  </div>
                </div>
                {/* Issue #166 FAC-09 — the VENUE's own condition, and the
                    one-tap way to change it.

                    #164 landed `grounds.condition` and this client rendered
                    it nowhere: the column shipped, the API sent the pair on
                    every ground, and no surface read it. So the read arrives
                    with the gesture, which is the only order that makes
                    sense — "update this rating" is not offerable next to a
                    rating nobody can see.

                    HIDDEN WHILE A CORRECTION IS OPEN. `onGroundCorrected`
                    closes the correction form, so a rating tap during an
                    unsaved edit would discard it; and the detail map is an
                    input in that state (#379). One write mode at a time. */}
                <ConditionUpdate
                  target={{ groundId: selected.ground_id }}
                  condition={selected.condition}
                  ratedAt={selected.condition_rated_at}
                  canEdit={canUpdateCondition(selected, user) && !correcting}
                  label="Condition"
                  testId={`ground-condition-${selected.ground_id}`}
                  onUpdated={onGroundCorrected}
                />
                {/* Issue #166 — FAC-02/03/04 read back: what is here, what it
                    is made of, what condition somebody last found it in and
                    WHEN they looked (#164 — an undated rating is worse than
                    none, because stale reads as current). FAC-09 adds the
                    per-surface re-rate to each row. */}
                {/* #547 — the ineligible arrival's sentence, in the spot the
                    surfaces render: "look, it's already here" IS the teaching.
                    `arrivalFor` decides on `canAddFacility`, so this line and
                    the auto-opened form are exclusive by construction. */}
                {surfaceArrival.note && <SurfaceInsteadNote ground={selected} />}
                {/* #583 cause 1: `pinDraft` must mean "a host map is actually
                    on screen", not "a host exists". Pass the draft ONLY when the
                    detail map is mounted — otherwise FacilityAddForm reads a
                    truthy pinDraft as proof of a map above it, suppresses its own
                    mini-map (`{!pinDraft && <SurfacePinMiniMap/>}`) and prints
                    "Tap the map above" beside a dialog with no map. Null here lets
                    the honest no-host branch run: the mini-map fallback (or
                    nothing for a centre-less venue) and the truthful copy. */}
                <FacilityList
                  ground={selected}
                  list={facilitiesForGround.get(selected.ground_id)}
                  canEdit={canUpdateCondition(selected, user) && !correcting}
                  onFacilityUpdated={onFacilityRated}
                  pinDraft={detailHasMap ? surfaceDraft : null}
                  onSurfaceFormOpen={() => detailMapRef.current?.scrollIntoView({ block: "nearest" })}
                  autoAdd={surfaceArrival.autoAdd}
                />
                {selected._played && selected._stats && (
                  <>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <MiniStat label="Matches" value={selected._stats.matches} />
                      <MiniStat label="Runs" value={selected._stats.runs ?? "—"} />
                      <MiniStat label="Wickets" value={selected._stats.wickets ?? "—"} />
                      <MiniStat label="Economy" value={selected._stats.economy ?? "—"} />
                    </div>
                    <GroundOversLeftOut n={selected._stats.overs_left_out} />
                    {/* #582 — the venue total above, then the per-field split. */}
                    <FacilityStatsBreakdown facilities={selected._stats.facilities} />
                  </>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {isPlayer && (
                    <Button onClick={(e) => toggleSaved(selected, e)} variant={savedIds.has(selected.ground_id) ? "default" : "outline"} data-testid={`save-detail-${selected.ground_id}`}
                      style={savedIds.has(selected.ground_id) ? { background: "#0B2E59" } : undefined}>
                      {savedIds.has(selected.ground_id) ? <><BookmarkCheck size={14} className="mr-2" /> Saved</> : <><Bookmark size={14} className="mr-2" /> Save to My Grounds</>}
                    </Button>
                  )}
                  {/* Issue #131 Tier 1. Offered on grounds this user created
                      and nobody has verified — the server owns the rest of
                      the decision (see lib/groundEdit.js). */}
                  {canOfferGroundEdit(selected, user) && !correcting && !surfaceDraft.active && (
                    <Button variant="outline" onClick={() => startCorrecting(selected)} data-testid={`edit-detail-${selected.ground_id}`}>
                      <Pencil size={14} className="mr-2" /> Correct details
                    </Button>
                  )}
                </div>
                {canOfferGroundEdit(selected, user) && correcting && (
                  <GroundEditPanel
                    ground={selected}
                    pin={correctionPin}
                    onPinChange={setCorrectionPin}
                    onCancel={stopCorrecting}
                    onSaved={onGroundCorrected}
                  />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <GroundSearchDialog
        open={addGroundOpen}
        onOpenChange={(o) => { setAddGroundOpen(o); if (!o) { setPendingPin(null); setAddGroundSeed(""); } }}
        initialQuery={addGroundSeed}
        initialTab={addGroundSeed ? "search" : null}
        presetCoords={pendingPin}
        onSaved={onGroundCreated}
        onAddSurfaceInstead={surfaceInstead.route}
      />
    </div>
  );
}

/**
 * Issue #765 / PR #858 review round 1 (F3) — the SHIPPED directory map,
 * extracted so it can be mounted at two different sizes (the directory
 * page's own 60vh card, and the redrawn screen's smaller band) without
 * being two different maps. Every prop name below is IDENTICAL to the
 * local variable `GroundsMap()` itself computes them as — `fit`,
 * `signature`, `fitReason`, `mappable`, `facilityPins`, `nearMe`, `q`, …
 * — on purpose: `GroundsMap.test.js`'s own `directoryMap()` helper pins
 * exact JSX substrings like `"<MapFitter fit={fit} signature={signature}
 * reason={fitReason} />"` and `"{mappable.map((g) => ("`, and matching
 * names is what keeps this extraction byte-identical to what those tests
 * have always pinned, rather than requiring them to be rewritten for a
 * refactor that changes no behaviour.
 *
 * `nearMe`/`onDrop` are the one deliberate seam: the shipped page's OWN
 * near-me (its filter-bar button, reading the viewport's `enriched`) and
 * the redrawn screen's OWN near-me (`useGroundsNearYou`'s independent,
 * lazy-loaded one) are two different states — this component takes
 * whichever one its caller is currently showing, as a prop, rather than
 * assuming there is only one near-me on the page.
 */
function DirectoryMapSurface({
  height,
  directoryBasemap,
  chooseBasemap,
  fit,
  signature,
  fitReason,
  holdFirstFit,
  onViewportChange,
  searching,
  cells,
  q,
  onDrop,
  nearMe,
  pendingPin,
  mappable,
  iconFor,
  facilitiesForGround,
  facilityPins,
  // PR #858 review round 3 (B3, third pass) — see `CellCountMarkers`'s own
  // header. Only the redrawn screen's call passes this; the shipped
  // page's own call below does not, so its bubbles sit at their true
  // position, byte-identical to before B3 touched this file.
  compact,
  // #1620: the redrawn screen's "show my location" (the same action as its "Use my location" button).
  onLocate,
}) {
  // #1619 (D439): the redrawn (`compact`) maps wrap the world and open on New Zealand; the shipped page keeps
  // #687's one clamped world. The overrides come last so the shipped props above stay as they were.
  const wrapWorld = Boolean(compact);
  // Pins and bubbles are drawn at the copy of their longitude nearest the LIVE view centre (`LiveCentreLng` below
  // keeps it current), not the fit's: a pan east past 180 jumps the centre to the other copy of the world.
  const [liveLng, setLiveLng] = useState(() => fitAnchorLng(fit, NZ_FALLBACK_CENTER[1]));
  const place = (latlng) => (wrapWorld ? placeLatLng(latlng, liveLng) : latlng);
  const placedCells = useMemo(() => (wrapWorld ? placeCells(cells, liveLng) : cells), [wrapWorld, cells, liveLng]);
  return (
    <MapContainer center={[20, 30]}
      zoom={2}
      maxBounds={[[-85, -180], [85, 180]]}
      maxBoundsViscosity={1}
      style={{ height, width: "100%" }}
      scrollWheelZoom
      {...(wrapWorld ? newLookMapProps() : null)}
    >
      <Basemaps active={directoryBasemap} onChoose={chooseBasemap} tapBox={Boolean(compact)} wrap={wrapWorld} pill={wrapWorld} />
      {/* #1620 (D439): the new-look maps' one control (locate, map/satellite, full screen) in place of the +/− box
          and the layers panel. */}
      {wrapWorld && <MapControlPill basemap={directoryBasemap} onBasemap={chooseBasemap} onLocate={onLocate} locating={nearMe.status === "locating"} />}
      {wrapWorld && <LiveCentreLng onChange={setLiveLng} />}
      <MapFitter fit={fit} signature={signature} reason={fitReason} refitOnResize={compact} fitFirstArrival={compact} holdFirstArrival={holdFirstFit} />
      {/* #643: the map asks for what is on screen. This is the ONLY
          thing that triggers a directory read — there is no mount
          fetch any more, because a fetch without a viewport is the
          unbounded read epic #609 exists to remove. */}
      <ViewportWatcher onViewport={onViewportChange} newLook={wrapWorld} />
      {/* #1193: the redrawn band's full-screen button resizes this map;
          re-frame the current fit for the new box. Not rendered on the
          shipped page, which has no such button. */}
      {compact && <MapFullScreenSync onResized={(map) => applyFit(map, fit, { reducedMotion: true })} />}
      {/* #642/#643: below the zoom threshold the answer is counts per
          area, not rows. Unconditional — the component draws nothing
          when there are no cells, which keeps the branch out of this
          function's complexity budget and inside something testable. */}
      {/* #644: no bubbles while a search is on. The list is the
          search's answer, and counts drawn for the frame the map
          happened to be in are a different question's answer sitting
          on top of it. */}
      <CellCountMarkers cells={searching ? [] : placedCells} declutter={compact} />
      {/* Issue #377: the marker popup wasn't dismissible by anything
          except clicking elsewhere on the map itself. See the
          component's own header for the root cause. */}
      <PopupDismissController searchTerm={q} />
      <PinDropCatcher onDrop={wrapWorld ? (pos) => onDrop({ ...pos, lng: wrapLngNear(pos.lng, 0) }) : onDrop} />
      {/* Issue #134: a marker, not a record. It exists for as long as
          this page does and is written nowhere. */}
      {nearMe.position && (
        <Marker position={place([nearMe.position.lat, nearMe.position.lng])} icon={youIcon} title="Your position">
          <Popup>
            <div className="w-44 text-xs">
              <div className="font-semibold text-slate-900">You&apos;re around here</div>
              <div className="text-slate-500 mt-0.5">Used on this device to sort the grounds below. Not saved or sent anywhere.</div>
            </div>
          </Popup>
        </Marker>
      )}
      {pendingPin && (
        <Marker position={[pendingPin.lat, pendingPin.lng]} icon={pendingIcon} title="Dropped pin">
          <Popup>
            <div className="w-52 text-xs">
              <div className="font-semibold text-slate-900 mb-1">Dropped pin</div>
              <div className="font-mono text-slate-600">{pendingPin.lat.toFixed(4)}, {pendingPin.lng.toFixed(4)}</div>
            </div>
          </Popup>
        </Marker>
      )}
      {mappable.map((g) => (
        <Marker key={g.ground_id} position={place(coordsOf(g))} icon={iconFor(g)} title={g.name}>
          <Popup>
            <div className="w-56">
              <div className="font-display font-bold text-sm text-slate-900">{g.name}</div>
              <div className="text-xs text-slate-500">{[g.city, g.country].filter(Boolean).join(", ")}</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                <PlaceTypeBadge g={g} />
                <SourceBadge g={g} />
                {g._distanceLabel && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 font-semibold">{g._distanceLabel} away</span>}
                {g._played && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{g._stats.matches} match{g._stats.matches !== 1 && "es"}</span>}
              </div>
              {/* Issue #166: what this venue is MADE OF, including
                  the surfaces that carry no pin of their own and so
                  are nowhere else on the map. */}
              <FacilitySummary g={g} list={facilitiesForGround.get(g.ground_id)} />
            </div>
          </Popup>
        </Marker>
      ))}
      {/* Issue #166 (FAC-06). Drawn AFTER the ground markers so a
          facility pinned on top of its venue's pin is the one a
          finger lands on — the surface pin is the authoritative
          position (schema.ts), so it wins the overlap. */}
      {facilityPins.map(({ facility: f, ground: g }) => (
        <Marker key={f.facility_id} position={place([f.latitude, f.longitude])} icon={facilityIcon(f)} title={`${f.name}, at ${g.name}`}>
          <Popup>
            <div className="w-56" data-testid={`facility-popup-${f.facility_id}`}>
              {/* #544: the pin says what it actually IS — its own
                  kind, never a guess. A pre-#544 row nobody has
                  typed reads as the neutral "Surface". */}
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: FACILITY_COLOR }}>
                {facilityKindLabel(f.kind) ?? "Surface"}
              </div>
              <div className="font-display font-bold text-sm text-slate-900">{f.name}</div>
              {/* The venue it belongs to, named — this pin is PART of
                  that ground, not a second ground beside it, and the
                  reader has to be able to tell those apart. */}
              <div className="text-xs text-slate-500">at {g.name}</div>
              <FacilityFacts f={f} />
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

function LegendChip({ color, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Issue #136 — the type, in words, wherever a ground is drawn.
 *
 * SHOWN ON `field` TOO, not just on the two interesting ones. A badge that
 * appears only for nets means a player scanning the list has to know that
 * absence means field — which is a rule they were never told, and which is
 * indistinguishable from the badge having failed to render. The ticket's
 * sentence is "a player scanning a nearby list needs to read field or nets
 * without opening anything", and that is the reading of it.
 *
 * Renders nothing when there is no type: a played venue that matched no
 * directory row (`place_type: null`) has nothing to say, and saying "Field"
 * would be inventing it.
 */
function PlaceTypeBadge({ g, large = false }) {
  const label = placeTypeLabel(g.place_type);
  if (!label) return null;
  const size = large ? "text-[11px] px-2.5 py-1" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`${size} rounded font-semibold inline-flex items-center gap-1 bg-slate-100 text-slate-700`}
      data-testid={`ground-type-${g.ground_id}`}
    >
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-slate-700 text-white text-[8px] font-black">
        {placeTypeGlyph(g.place_type)}
      </span>
      {label}
    </span>
  );
}

/**
 * Issue #166 — one line saying what a venue is MADE OF, wherever the venue
 * is drawn: the map popup and the directory card.
 *
 * IT COUNTS EVERY FACILITY, pinned or not, and that is the point of it
 * existing beside the markers. A surface with no coordinates of its own is
 * NOWHERE on the map (see `lib/facilities.js` for why it is not drawn at a
 * guessed position), so without this line "Torvale Club has three nets" would
 * be a fact the API holds and the page never says.
 *
 * Renders nothing when a venue has no facilities recorded — which is most of
 * them, and is not the same claim as "this venue has none". The absence of a
 * line is the absence of information; a "0 surfaces" badge on every ground
 * in the directory would be a page-wide assertion nobody has checked.
 */
function FacilitySummary({ g, list }) {
  /**
   * ISSUE #652 — THE DECISION, RECORDED HERE BECAUSE THIS IS WHERE THE LINE
   * IS RENDERED. It landed in two places, and both halves matter:
   *
   *   ON THE SERVER (option 2, without its stated cost): the viewport read
   *   returns an unpinned surface whose VENUE is in the box —
   *   `facilitiesInBoxWhere`, one more arm on a predicate whose query already
   *   joins `grounds`, so no second request. Without it, a club with two
   *   pinned nets and one unpinned read "2 surfaces": nullable coordinates
   *   (#137/#544) fail every SQL comparison, so an unpinned row was in no box
   *   at all. Right for markers, wrong for this line.
   *
   *   HERE (option 1, as the belt to that braces): the COUNT is
   *   `g.facility_count` — derived server-side over every row of the venue,
   *   unaffected by the box, the null pin or the row cap, and already on this
   *   row, so the correct number costs no request — and when the rows in hand
   *   are still fewer than it, the label says `+N more`. That case is now the
   *   CAP rather than the pin, and a capped list is short by an unknown
   *   amount, which is exactly when a number must not be quietly wrong.
   *
   * Option 3 (accept the under-report, reword to "2 pinned surfaces") was
   * rejected: a venue popup is about the venue, not about what happens to be
   * pinned. The arithmetic and the `+N more` marker are in
   * `lib/facilities.js`, tested against a venue holding one pinned and one
   * unpinned surface; the server arm is tested in
   * `api/test/groundFacilitiesBbox.test.ts`.
   */
  const label = facilitySummaryLabel(list, g.facility_count ?? 0);
  if (!label) return null;
  return (
    <div
      className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold"
      style={{ color: FACILITY_COLOR }}
      data-testid={`ground-facilities-${g.ground_id}`}
    >
      <LayoutGrid size={11} className="shrink-0" />
      {label}
    </div>
  );
}

/**
 * Issue #166 — surface, condition, and the two flags FAC-04 demanded be
 * answered explicitly rather than defaulted (`is_indoor`, `has_lighting`).
 *
 * THE RATING NEVER APPEARS WITHOUT ITS DATE, and that bargain is now kept by
 * `ConditionBadge` rather than here (#166 FAC-09). It moved when #164's VENUE
 * rating finally got a surface and this stopped being the only reader; the
 * argument for it lives with the component, once, so that a change to it
 * cannot reach one of the two places a rating is drawn and miss the other.
 */
function FacilityFacts({ f }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-slate-100 text-slate-700 inline-flex items-center gap-1">
        <span
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-white text-[8px] font-black"
          style={{ background: FACILITY_COLOR }}
        >
          {facilitySurfaceGlyph(f.surface)}
        </span>
        {facilitySurfaceLabel(f.surface) ?? f.surface}
      </span>
      {f.is_indoor && (
        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-slate-100 text-slate-700 inline-flex items-center gap-1">
          <Home size={10} /> Indoor
        </span>
      )}
      {f.has_lighting && (
        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-800 inline-flex items-center gap-1">
          <Lightbulb size={10} /> Lights
        </span>
      )}
      {/* #166 FAC-11. Only the map page asks the API for archived surfaces,
          and only to say this here — the pickers' default read never
          carries one. */}
      <ArchivedBadge g={f} />
      {/* Issue #166 FAC-09. The badge moved to `ConditionUpdate.jsx` when
          #164's VENUE rating finally got a surface and became its second
          reader — one component, so no surface can render a rating without
          the date that says whether it is still news, and "Not rated" says
          the state rather than leaving a blank that reads as a bug. */}
      <ConditionBadge
        condition={f.condition}
        ratedAt={f.condition_rated_at}
        testId={`facility-condition-${f.facility_id}`}
      />
    </div>
  );
}

/**
 * Issue #166 — the venue's facilities in full, in the detail dialog.
 *
 * Says explicitly which surfaces are NOT on the map. A reader who can see
 * two violet pins and a list of four has to be told why, or the map looks
 * like it dropped two — and the honest answer ("nobody has pinned them
 * individually yet") is also an invitation to go and do it.
 */
/**
 * Issue #166: the venue's own pinned surfaces, on the map you opened to see
 * the venue. The caller hides them while a CORRECTION is open (#379) or
 * while a SURFACE PIN is being placed (#548) — in both states that map has
 * become an input, and a second set of tappable markers on it is a way to
 * grab the wrong pin. Unpinned rows are filtered, never invented onto the
 * map (`lib/facilities.js`'s rule), so a surface pinned through the add
 * form appears here the moment its server row folds back in — and not a
 * moment before.
 */
function DetailFacilityMarkers({ list }) {
  return (list || [])
    .filter(hasFacilityPin)
    .map((f) => (
      <Marker key={f.facility_id} position={[f.latitude, f.longitude]} icon={facilityIcon(f)} keyboard={false} />
    ));
}

// Issue #766 — exported (unchanged otherwise) so `components/GroundCard.jsx`
// can mount the SAME per-facility list/ratings/edit/add machinery behind
// its own "Rate the surface" toggle (D167 G9: "opens the shipped per-
// facility list and ratings as a sub-panel, so journey 19's testids stay
// reachable"), rather than a second implementation that could drift from
// this one.
export function FacilityList({ ground, list, canEdit = false, onFacilityUpdated, pinDraft = null, autoAdd = null, onSurfaceFormOpen = null }) {
  // #211 — visitor ratings for every surface at this venue, one fetch for
  // the whole list. Renders nothing until the read answers, so a failed or
  // in-flight read never claims "No ratings yet" about surfaces it has not
  // heard about. (Hook order: called before the early return below.)
  const ratings = useGroundRatings(ground?.ground_id);
  // #544's grouping: fields first, then nets, then the pre-#544 rows nobody
  // has typed — one section, ordered the way its header reads.
  const rows = sortFacilitiesByKind((list || []).filter(Boolean));
  /**
   * Issue #542 — the empty state stops being silence. This used to return
   * `null` on zero rows, which was correct as a claim (absence of
   * information) and fatal as a product: no client path created a facility
   * (#165 shipped API-only), so on any unseeded ground the whole
   * ratings/condition machinery (#211/#540) was unreachable and invisible.
   * Three deliberate states now, decided by `facilityEmptyState`
   * (lib/facilityAdd.js) on #271's discipline:
   *   - can-add: the invitation plus the add form, gated by the SAME
   *     `canEdit` the condition update rides in on (`canUpdateCondition`
   *     at the call site = `canOfferGroundEdit` = the server's
   *     `checkGroundCorrectionGate`, mirrored — never a control that 403s);
   *   - cannot-add: the honest copy, so a player can at least see the
   *     feature exists;
   *   - unknown: NOTHING — the facilities read failed or has not answered
   *     (the directory's own `facility_count` disagrees with the empty
   *     list), or this is a played-only row the API sends no facilities
   *     for. "No fields here" about a failed fetch is #271's exact lie.
   */
  if (rows.length === 0) {
    const empty = facilityEmptyState(ground, canEdit);
    if (empty === "unknown") return null;
    return (
      <div className="mt-5" data-testid={`ground-facility-empty-${ground.ground_id}`}>
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">
          <LayoutGrid size={12} style={{ color: FACILITY_COLOR }} />
          Fields &amp; Nets
        </div>
        <p className="mt-1.5 text-xs text-slate-500">
          {FACILITY_EMPTY_COPY}{empty === "invite" ? " — record the first one, and its condition and ratings arrive with it." : "."}
        </p>
        {empty === "invite" && (
          <FacilityAddForm ground={ground} onAdded={onFacilityUpdated} pinDraft={pinDraft} autoOpen={autoAdd} onFormOpen={onSurfaceFormOpen} testId={`facility-add-${ground.ground_id}`} />
        )}
      </div>
    );
  }
  const unpinned = rows.filter((f) => !hasFacilityPin(f)).length;
  return (
    <div className="mt-5" data-testid={`ground-facility-list-${ground.ground_id}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">
        <LayoutGrid size={12} style={{ color: FACILITY_COLOR }} />
        Fields &amp; Nets ({rows.length})
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((f) => (
          <div key={f.facility_id} className="rounded-md border border-slate-200 p-2.5" data-testid={`facility-row-${f.facility_id}`}>
            <div className="font-semibold text-sm text-slate-900 truncate">{f.name}</div>
            <FacilityFacts f={f} />
            {/* Issue #601 — correct what the line above says: the name, the
                field/net, the surface. In place, so the row keeps its id and
                with it its ratings, its history and every training session
                that points at it — the add-and-archive workaround this
                replaces lost all three. Same gate as every other write on
                this row: a facility has no gate of its own
                (`routes/groundGate.ts`), so `canEdit` (the parent ground's
                correction gate, mirrored) decides all of them together. */}
            {canEdit && (
              <FacilityEditForm
                ground={ground}
                facility={f}
                onSaved={onFacilityUpdated}
                testId={`facility-edit-${f.facility_id}`}
              />
            )}
            {/* Issue #166 FAC-09 — re-rate this net without re-submitting
                what it is made of. Offered only when the parent ground's
                correction gate would let this person through, because a
                facility has no gate of its own (`routes/groundGate.ts`). */}
            {canEdit && (
              <ConditionUpdate
                target={{ groundId: ground.ground_id, facilityId: f.facility_id }}
                condition={f.condition}
                ratedAt={f.condition_rated_at}
                canEdit
                label="Update condition"
                testId={`facility-condition-update-${f.facility_id}`}
                onUpdated={onFacilityUpdated}
              />
            )}
            {/* #211 — what the people who played here found, beside what
                the record-keeper says it is (the ConditionUpdate above):
                siblings answering different questions, deliberately. The
                rate control appears only when the server said the PUT
                would accept this caller (can_rate) — never a 403 button. */}
            {ratings.data && (
              <FacilityRatingPanel
                groundId={ground.ground_id}
                facilityId={f.facility_id}
                surface={f.surface}
                kind={f.kind ?? null}
                entry={ratings.byFacility.get(f.facility_id)}
                canRate={ratings.data.can_rate}
                playedThisSurface={(ratings.data.played_facility_ids || []).includes(f.facility_id)}
                currentSeason={ratings.data.current_season}
                archived={Boolean(f.archived_at)}
                onSaved={ratings.apply}
              />
            )}
            {/* #166 FAC-11/12 — the torn net: archived, not deleted, so the
                sessions trained on it keep their surface (the API refuses
                the delete once anything references it, `facility_in_use`).
                Same gate as the rating above — no facility gate of its own. */}
            {canEdit && (
              <ArchiveToggle
                basePath={`/grounds/${ground.ground_id}/facilities/${f.facility_id}`}
                archived={Boolean(f.archived_at)}
                noun="surface"
                onDone={onFacilityUpdated}
                testId={`facility-archive-${f.facility_id}`}
              />
            )}
          </div>
        ))}
      </div>
      {unpinned > 0 && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid={`ground-facility-unpinned-${ground.ground_id}`}>
          {unpinned} of these {unpinned === 1 ? "has" : "have"} no pin of {unpinned === 1 ? "its" : "their"} own yet, so
          {unpinned === 1 ? " it is" : " they are"} not drawn separately on the map — {unpinned === 1 ? "it sits" : "they sit"} at this venue.
        </p>
      )}
      {/* #542 — the second net is as unrecordable as the first without this.
          Same gate as every facility write on this list (a facility has no
          gate of its own — `routes/groundGate.ts`), same fold-back as the
          re-rate, so a new row arrives with its condition control and #211
          rating block and no reload. */}
      {canEdit && (
        <FacilityAddForm ground={ground} onAdded={onFacilityUpdated} pinDraft={pinDraft} autoOpen={autoAdd} onFormOpen={onSurfaceFormOpen} testId={`facility-add-${ground.ground_id}`} />
      )}
    </div>
  );
}

/**
 * Issue #166 FAC-11 — the archived state, said wherever the source is said.
 * Rendered as a SIBLING of the source badge rather than replacing it: an
 * archived ground is still yours/global/verified, and "Archived" answers a
 * different question (is it in use?) than provenance does (whose is it?).
 */
function ArchivedBadge({ g, large = false }) {
  if (!isArchived(g)) return null;
  const size = large ? "text-[11px] px-2.5 py-1" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`${size} rounded font-semibold inline-flex items-center gap-1 bg-slate-200 text-slate-600`}
      data-testid={`archived-badge-${g.ground_id ?? g.facility_id}`}
    >
      <Archive size={10} /> {ARCHIVED_LABEL}
    </span>
  );
}

/**
 * Issue #606 — "nobody else can see this", said on the map too.
 *
 * `SourceBadge` below answers WHERE A ROW CAME FROM, and for a private pin
 * it answers "Your ground" — true, and not the same sentence as "only you
 * can see this". An admin building the shared directory read the first and
 * assumed the second, and 27 venues sat invisible for a day (#606).
 *
 * Every private ground in this page's data is the caller's own — the
 * directory read serves nobody else's — so the label is always accurate
 * here. It rides alongside the source badge exactly as `ArchivedBadge`
 * does, so it appears on every surface that draws one: the cards, the map
 * popups and the detail dialog.
 */
function PrivateBadge({ g, large = false }) {
  if (!isPrivateGround(g)) return null;
  const size = large ? "text-[11px] px-2.5 py-1" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`${size} rounded font-semibold inline-flex items-center gap-1 bg-amber-100 text-amber-800`}
      data-testid={`private-badge-${g.ground_id}`}
    >
      <EyeOff size={10} /> {PRIVATE_GROUND_LABEL}
    </span>
  );
}

function SourceBadge({ g, large = false }) {
  const size = large ? "text-[11px] px-2.5 py-1" : "text-[10px] px-1.5 py-0.5";
  const source = (() => {
    if (g._played)
      return <span className={`${size} rounded font-semibold inline-flex items-center gap-1`} style={{ background: "#D4A01720", color: "#8A6D0A" }}>🏆 Played here</span>;
    if (g._authored)
      return <span className={`${size} rounded font-semibold inline-flex items-center gap-1`} style={{ background: "#3B82F620", color: "#1D4ED8" }}><UserIcon size={10} /> Your ground</span>;
    if (g.is_global && g.is_verified)
      return <span className={`${size} rounded font-semibold inline-flex items-center gap-1`} style={{ background: "#10B98120", color: "#065F46" }}><ShieldCheck size={10} /> Verified</span>;
    if (g.is_global)
      return <span className={`${size} rounded font-semibold inline-flex items-center gap-1`} style={{ background: "#0B2E5915", color: "#0B2E59" }}><Globe2 size={10} /> Global</span>;
    return <span className={`${size} rounded font-semibold inline-flex items-center gap-1`} style={{ background: "#64748B20", color: "#334155" }}>Community</span>;
  })();
  // #166 FAC-11: archived travels with the source, everywhere the source is
  // drawn — cards, popups, the detail dialog — so no surface on this page
  // can render an archived venue as an ordinary one.
  return (
    <>
      <ArchivedBadge g={g} large={large} />
      {/* #606: unpublished travels with the source for archived's reason —
          no surface on this page can render a private pin as an ordinary
          one. A facility row has no `ground_id` and no flags of its own, so
          this is a no-op there. */}
      <PrivateBadge g={g} large={large} />
      {source}
    </>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-2 rounded-md bg-slate-50">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="font-display font-bold text-sm text-slate-900">{value}</div>
    </div>
  );
}

/**
 * Issue #582 — a venue's results per field/net, so the card and the detail
 * dialog show more than one lumped venue total. Rows come from
 * `buildFacilityStats` (already merged + sorted); this only renders them.
 *
 * The NULL-facility row (`facility_id === null`) is Martin's expectation to
 * set: facility_id was NEVER backfilled onto historic matches (#546), and
 * #581 keeps it sparse going forward (pick-only, no inline creation), so
 * nearly every legacy match lands here. That is CORRECT, not a bug — it
 * renders as a plain labeled "Field not recorded" row, never an error and
 * never a field literally named "Unknown". Self-hides when a venue has no
 * facility split at all.
 */
function FacilityStatsBreakdown({ facilities }) {
  if (!facilities || facilities.length === 0) return null;
  return (
    <div className="mt-4" data-testid="facility-stats-breakdown">
      <div className="text-[10px] uppercase text-slate-500 mb-1.5">By field / net</div>
      <div className="space-y-1.5">
        {facilities.map((f) => {
          const notRecorded = f.facility_id === null;
          return (
            <div
              key={f.facility_id ?? "not-recorded"}
              className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-2.5 py-1.5"
              data-testid={`facility-stat-${f.facility_id ?? "not-recorded"}`}
            >
              <div className="min-w-0">
                <div className={`text-xs font-semibold truncate ${notRecorded ? "text-slate-500 italic" : "text-slate-800"}`}>
                  {notRecorded ? "Field not recorded" : f.facility_name || "Unnamed field"}
                </div>
                {!notRecorded && f.facility_surface && (
                  <div className="text-[10px] text-slate-500 capitalize">{f.facility_surface}</div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0 text-[11px] text-slate-600">
                <span><span className="font-display font-bold text-[#0B2E59]">{f.runs ?? "—"}</span> runs</span>
                <span><span className="font-display font-bold text-slate-700">{f.wickets ?? "—"}</span> wkts</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GroundCard({ g, isPlayer, facilities, onOpen, onToggleSave }) {
  return (
    <Card
      onClick={onOpen}
      className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden group relative h-full"
      data-testid={`ground-card-${g.ground_id}`}
    >
      <CardContent className="p-5 flex flex-col h-full">
        {/* #608 journey 19c: `flex-wrap` on the ROW, not just the badge
            group. #606 added a fourth badge (Private) and at 390px the
            title plus badges exceed the card by ~2px, so without a wrap
            here the row overflows the viewport instead of dropping to a
            second line. `min-w-0` lets the truncating title actually
            truncate once it shares a line.

            This comment used to say `shrink-0` on the badge group kept the
            badges legible. It did not — see the group's own note below:
            `shrink-0` sized the group at max-content, which is the reason
            it could never drop to a second line either. Legibility comes
            from the badges themselves never shrinking, which is still
            true. (#621) */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="font-display font-bold text-lg text-slate-900 truncate min-w-0">{g.name}</h3>
          {/* #621: `min-w-0` and NO `shrink-0`. The group already had
              `flex-wrap`, and it never fired — a `shrink-0` flex item is
              sized at max-content, so the badges could not wrap and the
              group simply overflowed the card. On a PRIVATE ground it
              carries three badges ("Field", "Your ground", "Private — only
              you can see this") and measures 324px; the card's content box
              is 161px in the two-column grid at 768px, so it hung 163px
              past the card — and, in the right-hand column, 107px past the
              page. It had been overflowing the card at every width since
              #606 added the third badge and was invisible because the
              harness's directory had no rows in it at all (#621). Shrinking
              is what lets `flex-wrap` put the badges on a second line;
              nothing shrinks a badge itself. */}
          <div className="flex flex-wrap items-center gap-1 min-w-0">
            <PlaceTypeBadge g={g} />
            <SourceBadge g={g} />
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1"><MapPin size={12} /> {[g.city, g.country].filter(Boolean).join(", ") || "—"}</div>
        {/* Issue #133: the ordering is only trustworthy with the number on it. */}
        {g._distanceLabel && (
          <div className="text-xs font-semibold text-teal-700 mt-1" data-testid={`ground-distance-${g.ground_id}`}>
            {g._distanceLabel} away
          </div>
        )}
        <FacilitySummary g={g} list={facilities} />
        {g._played && g._stats ? (
          <>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[10px] uppercase text-slate-500">Matches</div><div className="font-display font-bold text-[#D4A017]">{g._stats.matches}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Runs</div><div className="font-display font-bold text-[#0B2E59]">{g._stats.runs ?? "—"}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Wickets</div><div className="font-display font-bold text-slate-700 text-sm">{g._stats.wickets ?? "—"}</div></div>
            </div>
            {/* #582 — per-field split under the venue total (self-hides if none). */}
            <FacilityStatsBreakdown facilities={g._stats.facilities} />
          </>
        ) : null}
        {/* #707: an in-flow footer row, not `absolute bottom-3 right-3` (which
            took the pill out of the layout and covered the last field row's
            runs/wkts). `mt-auto` pins it to the bottom of every card in a
            stretched grid row. */}
        {isPlayer && (
          <div className="mt-auto flex justify-end pt-3">
            <button
              type="button"
              onClick={onToggleSave}
              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border transition ${g._saved
                ? "bg-[#0B2E59] text-white border-[#0B2E59]"
                : "bg-white text-[#0B2E59] border-[#0B2E59] hover:bg-[#0B2E5908]"}`}
              data-testid={`save-${g.ground_id}`}
              title={g._saved ? "Remove from My Grounds" : "Add to My Grounds"}
            >
              {g._saved ? <><BookmarkCheck size={12} /> Saved</> : <><Bookmark size={12} /> Save</>}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
